import { Component, JSX } from 'react';
import Router from 'next/router';
import { toReadableBytes } from '../../../utils/file';
import { previewKindFor, PreviewKind } from '../../../utils/preview-kind';
import { formatOptionEcho } from '../../../utils/option-echo';
import { chainToTool } from '../../../services/chain-to-tool.service';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { container } from '../../../context/module';
import { MetricsService } from '../../../services/metrics.service';
import { MetricEventName } from '../../../types/metric-events';
import { suggestionsFor } from '../../../utils/next-tool';
import { canPreviewOutput, CapabilityResult } from '../../../utils/media-capability';

import { lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { GAService } from '../../../services/analytics.service';
import { EnvironmentService } from '../../../services/environment.service';
import { EnforcementErrorService } from '../../../services/enforcement-error.service';
import { FileDownloadService } from '../../../services/file-download.service';
import { FileManagerService } from '../../../services/file-manager.service';
import { FileProcessService } from '../../../services/file-process.service';
import { ProcessCompleteCheckService } from '../../../services/process-complete-check.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { scheduleCoordinator } from '../../../services/scheduling/schedule-coordinator';
import { scheduleInFlight } from '../../../services/scheduling/schedule-in-flight';
import { SettingsService } from '../../../services/settings.service';
import { SpinnerService } from '../../../services/spinner.service';
import { StorageService } from '../../../services/storage.service';
import { UIContext } from '../../../services/ui-context';
import { GAContants } from '../../../types/ga.types';
import { OptionalChildrenProps } from '../../../types/react.types';
import { RouteProps } from '../../../types/route-types';
import {
    ProcessStageSubStateTypes,
    ProcessStageTypes,
    ProcessTypes,
    StatusTypes,
    WebsocketProcessTypes,
    WebsocketResponse,
} from '../../../types/types';
import { ConvertOptionsUtils } from '../../../utils/convert-options-utils';
import { ConvertOptionsV2Utils } from '../../../utils/convert-options-v2-utils';
import { PSDate, PSRedableDuration } from '../../../utils/date.utils';
import { getExtension, replaceExtension } from '../../../utils/file';
import { get, isNaN, isNil, isNumber, isString, toInteger } from '../../../utils/lodash-min';
import { SafeExecUtils } from '../../../utils/safe-exec.utils';
import { StringUtils } from '../../../utils/string-utils';
import { untilDestroyed } from '../../../utils/until-destroyed';
import { studioCardFileName, studioCardLine } from '../../studio/export/card';
import { STUDIO_CONTAINER_EXT } from '../../studio/export/build-request';
import { STUDIO_VIDEO_CODEC } from '../../studio/export/output-format';
import { isStudioWorkflow, openStudioReedit, studioCompositionOf, studioProjectStillOpen, studioReeditHref } from '../../studio/export/reedit';
import { newProjectId } from '../../studio/model/composition';
import { newStudioHref } from '../../studio/state/studio-url';
import { rehydrateStudioHref } from '../../studio/state/rehydrate';
import { STUDIO_TTL_MS } from '../../studio/state/persistence';
import {
    FRAME_PICKER_LABELS_DROPPED_COPY, FrameBadgeFacts, FramePickerNote, frameBadgeFacts, framePickerNoteOf, framePickerWorkflowNoteOf, isFramePickerWorkflow, pickAnotherFrameHref, reeditRouteOf, REEDIT_EXPIRED_COPY, ReeditRoute, startNewPickerHref, cardSlugOf, pickerCardVerdict, recordPickerJobFinish, pickerFrameLabelForWorkflow, pickerFrameTimeForWorkflow, pickerJobRangeForWorkflow, pickerRangeBadgeForWorkflow, pickerSidForWorkflow, pickerThumbForWorkflow, pickerOutputFileName,
    pickerThumbSource, withoutFramePickerNotes,
} from './frame-picker-card';
import { FileUploadReEditModalComponent } from '../file-upload-reedit/file-upload-reedit-modal.component';
import { MediaPreviewModalComponent } from '../media-preview/media-preview-modal.component';
import Modal from '../modal/modal.component';
import ProgressComponent from '../progress/progress.component';
import { RippleLoaderComponent } from '../ripple-loader/ripple-loader.component';
import { SingleFileDownloadModalComponent } from '../single-fle-download/single-file-download.component';
import Tooltip from '../tooltip/tooltip.component';
import styles from './file-list-item.component.module.scss';

declare var gapi;
declare var Dropbox;

/**
 * The one suggestion in `utils/next-tool.ts` that is behind a feature flag.
 *
 * `/pdf-editor` renders "Editing PDF text in your browser is on the way" while
 * `settings.featureFlags.pdfEditor` is off (docs/proposed/pdf-editor-frontend.md §9.1, label
 * row `flag.soon`), so a chip offered with the flag off carries the user's file to a page that
 * cannot open it. The suggestion table stays flag-free — it is a ranking, not a gate — and the
 * gate lives here, at the point of use.
 */
const EDIT_PDF_SLUG = 'pdf-editor';

interface FileListItemComponentProps extends RouteProps, OptionalChildrenProps {
    statusCheckButtonEnabled?: boolean;
    reeditButtonEnabled?: boolean;
    deleteButtonEnabled?: boolean;
    fileUpload?: FileUpload;
    hideFooterButtons?: boolean;
}
interface FileListItemComponentState {
    zipDownloadInProgress: boolean;
    linkCopied: boolean;
    creditOpen: boolean;
    creditFormat: 'html' | 'markdown' | 'plain';
    creditCopied: boolean;
    /** R14 ask 9: the picker thumbnail's `onerror` verdicts -- the edge poster failed (show the pressed frame if any), or nothing renders. */
    pickerThumbEdgeFailed?: boolean;
    pickerThumbHidden?: boolean;
    /** Editor re-open T3 (R-E): the editor key found nothing to re-open -- the card says so and offers one action. */
    reeditExpired?: 'studio' | 'picker' | null;
    /** Review 7 K7: this card's ZIP build answered success in this page, so a click downloads it without asking the edge again. */
    zipBuilt?: boolean;
    /** Review 7 K7: the build outlived the click's user activation, so the ZIP waits for the next click (a download with no gesture is blocked). */
    zipAwaitingClick?: boolean;
    /** Review 7 S3-02: the never-started bound has elapsed on this page (the card still has to BE unstarted to show it). */
    unstartedBoundPassed?: boolean;
}

/**
 * Review 7 K7: may a download start NOW? Chrome (and Safari/Firefox) let `window.open` -- and so a download -- through only
 * inside a user gesture; the activation lapses about 5 s after the click. Measured on QA: the ZIP build for 54 1080p frames
 * answered 7.6 s after the click, and the old handler opened the ZIP with `isActive === false`, which the popup and
 * automatic-downloads blockers stop. A browser without the API keeps the old behaviour (attempt; the card toasts a block).
 */
/**
 * Review 7 S2-04: text for an HTML-string sink (the "Download Links" dialog). A card's name is the user's own text -- a dropped
 * file's name, a typed Studio name in any script -- so it is escaped where it meets markup instead of being reduced to ASCII.
 */
function escapeHtml(text: unknown): string {
    return String(text ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[ch] as string));
}

/**
 * Review 7 S3-02: how long a card may say "Process starting…" with NOTHING behind it -- no server-side workflow, no edge message,
 * no result, no scheduling coordinator holding it, no upload running. `processFile` persists the card BEFORE the schedule call
 * (file-process.service.ts `_processFileInternal`), so a refused or abandoned schedule (QA: a 409 retry loop, then the page was
 * left) leaves a card that no message will ever move. The bound sits above the slowest honest start the client allows: the
 * flag-off schedule call is 3 attempts x 30 s timeout + 2 x 1 s backoff (~92 s); every other path reports within seconds.
 */
export const NEVER_STARTED_AFTER_MS = 120_000;
export const NEVER_STARTED_COPY = 'This job never started, so nothing was converted. Start it again, or remove this card.';

/**
 * WC-09 (review 7): a browser WITHOUT `navigator.userActivation` (Safari < 16.4) cannot tell us the click is still usable,
 * and after a long ZIP build it usually is not -- `window.open` was popup-blocked. Unknown is therefore NO: the card takes
 * its ready-state button path (the user's next click opens the ZIP), which works everywhere.
 */
export function hasTransientActivation(): boolean {
    const ua = typeof navigator !== 'undefined' ? (navigator as any).userActivation : undefined;
    return !!ua && typeof ua.isActive === 'boolean' ? ua.isActive : false;
}

export class FileListItemComponent extends Component<FileListItemComponentProps, FileListItemComponentState> {
    private readonly logger = Logger.getLogger('FileListItemComponent');
    readonly FILEUPLOAD_STATE_FAILED = StatusTypes.FAILED;

    fileProcessInfo: string;
    fileOutputInfo: string | JSX.Element;


    get fileUpload() {
        return this.props.fileUpload;
    }

    get hasZipFileInfo() {
        return this.props.fileUpload && this.props.fileUpload.zipResult;
    }

    get zipSupported() {
        return this.fileUpload && (this.fileUpload.processType === ProcessTypes.SPLIT ||
            (this.fileUpload.ws && this.fileUpload.ws.processType === WebsocketProcessTypes.CONVERT_ONE_TO_MANY));
    }

    get canRequestZipFile() {
        return this.zipSupported && !this.fileUpload.zipResult;
    }

    get isZipFileReady() {
        return this.hasZipFileInfo && this.fileUpload.zipResult.processStageSubState === ProcessStageSubStateTypes.COMPLETED;
    }

    get hasZipFileData() {
        if (!this.hasZipFileInfo) {
            return 'No data';
        }
        if (this.fileUpload.zipResult.processStageSubState === ProcessStageSubStateTypes.COMPLETED) {
            return 'File zipped successfully';
        }
        return this.fileUpload.zipResult.data;
    }

    get statusCheckButtonEnabled() {
        return isNil(this.props.statusCheckButtonEnabled) || this.props.statusCheckButtonEnabled;
    }
    get reeditButtonEnabled() {
        return isNil(this.props.reeditButtonEnabled) || this.props.reeditButtonEnabled;
    }
    get deleteButtonEnabled() {
        return isNil(this.props.deleteButtonEnabled) || this.props.deleteButtonEnabled;
    }

    get hasMultipleFilesToDownload(): boolean {
        return this.fileUpload && this.fileUpload.result && Array.isArray(this.fileUpload.result.output);
    }

    /**
     * The Frame picker's context for THIS card, or `null` for an ordinary conversion
     * (FRAME_PICKER_DESIGN.md §4.1, §6.5). Read off the result's `processingNotes` — the one
     * tagged line ironman writes — so it survives a reload and a recovered card alike, unlike
     * anything held in page state. One predicate for the badge, the ZIP primary, the link and
     * the `FILE_DOWNLOAD{source}` attribute, so none of them can disagree.
     */
    get framePickerNote(): FramePickerNote | null {
        // Q16: while the job is queued the note does not exist yet; the request says the same
        // thing, gated on the picker having recorded this workflow (Quick convert writes the
        // same keys). The picker's own storage read never throws (`pickerSidForWorkflow`).
        // The note and the request carry the SEEK instant. Which time the badge SAYS is the badge's own decision
        // (`frameBadgeFacts` → `frameBadgeText`, R14Q-14/16): the server's once the result has landed, else the user's
        // on a CFR clip, else the wire -- this getter no longer swaps the user's time in for every reader.
        // Editor re-open T3: ONE predicate (`framePickerWorkflowNoteOf` / `isFramePickerWorkflow`) for this getter, the
        // editor key, Re-edit's destination and `FILE_DOWNLOAD{source}`.
        return framePickerWorkflowNoteOf(this.props.fileUpload, this.storageService);
    }

    /**
     * R14 (R14Q-14 / 15 / 16): what the badge may know about this card's frame -- the LANDED note (null while queued; the
     * request-side stand-in is not the server speaking), the result (for the saved name), the seek instant and the user's
     * time under that seek (`frameTimes`, IT2). The card module orders them.
     */
    private frameBadgeFactsOf(fileUpload: FileUpload): FrameBadgeFacts {
        const landed = framePickerNoteOf(fileUpload?.result?.processingNotes);
        const seek = this.pickerSeekOf(fileUpload);
        return frameBadgeFacts(landed, fileUpload?.result, seek, pickerFrameTimeForWorkflow(fileUpload?.workflowId, seek, this.storageService));
    }

    /** A picker sequence -- or, editor re-open T5, the pinned frames as one job: many outputs, the ZIP is the thing the user came for (§6.5). */
    get isPickerSequence(): boolean {
        const mode = this.framePickerNote?.mode;
        return (mode === 'SEQUENCE' || mode === 'PICKED') && !!this.zipSupported;
    }

    @lazyInject(FileDownloadService) private fileDownloadService: FileDownloadService;
    @lazyInject(GAService) private gaService: GAService;
    @lazyInject(MetricsService) private metricsService: MetricsService;
    @lazyInject(UIContext) private uiContext: UIContext;
    @lazyInject(ProcessCompleteCheckService) private processCompleteCheckService: ProcessCompleteCheckService;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(SpinnerService) private spinner: SpinnerService;
    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(FileProcessService) private fileProcessService: FileProcessService;
    @lazyInject(EnforcementErrorService) private enforcementErrorService: EnforcementErrorService;
    @lazyInject(EnvironmentService) private envService: EnvironmentService;
    @lazyInject(SettingsService) private settingsService: SettingsService;
    @lazyInject(StorageService) private storageService: StorageService;

    readonly state: Readonly<FileListItemComponentState> = { zipDownloadInProgress: false, linkCopied: false, creditOpen: false, creditFormat: 'html', creditCopied: false };

    /**
     * Suggestions that need a browser-decodable image, once the runtime check has answered.
     * Until then they are withheld: offering "Resize Image" for a TIFF sends the user to a
     * canvas editor that cannot open it and silently shows its landing page instead.
     */
    private editorSafe: boolean | null = null;

    /**
     * Is `/pdf-editor` live for this visitor? Withheld until we know, for the same reason as
     * `editorSafe`: a chip to a "coming soon" page is worse than one chip fewer.
     *
     * Read from the TOP-LEVEL `settings.featureFlags` (never `userSettings.featureFlags`, dead
     * since 2026-05) through the bare `featureFlags` observable rather than the provenance-carrying
     * `featureFlags$`. The mount gate on `/pdf-editor` needs provenance because a props-baked PROD
     * `false` would otherwise pin it to a placeholder for the whole session; a suggestion chip
     * does not, because this subscription lives for the card's lifetime and the API emission
     * simply turns the chip on when it lands. Strict `=== true` — never `!!ff?.flag`.
     */
    private pdfEditorEnabled = false;

    /** Guards the async capability probe from updating a card the user already deleted. */
    private mounted = false;

    /** Slug being chained from THIS card, so only its own chip shows progress. */
    private chaining: string | null = null;
    /** Why the file did not travel, shown on this card rather than as a page-level toast. */
    private chainNotice: string | null = null;

    /**
     * Clear the busy flag when the page comes back.
     *
     * Chaining navigates away, so nothing ever cleared `chaining` on the success path -- and a
     * browser Back restores this page from the bfcache without remounting, so the flag survived.
     * The chip stayed on "Taking your file…" and `if (this.chaining) return` then swallowed
     * every later click on every chip. Reported as "none of those pills are clickable".
     */
    private resetChainState = () => {
        if (!this.mounted) return;
        if (this.chaining || this.chainNotice) {
            this.chaining = null;
            this.chainNotice = null;
            this.forceUpdate();
        }
    };

    getZipFileURL(fileInfo: FileUpload) {
        if (!fileInfo.result) {
        } else if (fileInfo.result.error === true) {
        } else {
            try {
                const tokens = fileInfo.result.zip.split('\\');
                return this.fileDownloadService.getDownloadURL(fileInfo, tokens[tokens.length - 1]);
            } catch (e) {
                return;
            }
        }
    }

    componentDidMount() {
        this.armNeverStartedTimer();
        this.mounted = true;
        const toType = this.props.fileUpload?.toType;
        if (toType) {
            canPreviewOutput(`${toType}`)
                .then(verdict => {
                    // 'unknown' counts as safe: the editor is the one that will find out, and
                    // withholding a useful destination on a maybe is the worse error.
                    this.applyEditorSafe(verdict !== 'no');
                })
                .catch(() => this.applyEditorSafe(true));
        }
        if (BrowserUtils.hasWindow()) {
            // `pageshow` fires on a bfcache restore, where componentDidMount does not run again.
            window.addEventListener('pageshow', this.resetChainState);
            window.addEventListener('visibilitychange', this.resetChainState);
        }
        SafeExecUtils.execNoLog(() => {
            this.settingsService.featureFlags
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: ff => this.applyPdfEditorEnabled(ff?.pdfEditor === true),
                    // A flag we cannot read leaves the chip withheld; it is a suggestion.
                    error: () => this.applyPdfEditorEnabled(false),
                });
        });
        // Hands-off scheduling: the queued/retrying/stalled line is driven by a
        // TIMER-backed state machine, and nothing in the FileUpload changes when
        // the watchdog fires — so the card has to hear it from the coordinator
        // (2026-09-05 incident; MONGO-STALL-PLAN-v5.2.md §4.4). No-op while the
        // flag is off: the store has no cards and never notifies.
        this.unsubscribeSchedule = scheduleCoordinator.subscribe(() => {
            if (this.mounted) this.forceUpdate();
        });
        // R14-SH16: a card that mounts already finished / failed (a reload of /downloads) reports its verdict now.
        this.reportPickerJobFinish();
    }

    /** R14-SH16: the transition -- the socket or the result moving this card to done / failed while it is on screen. */
    componentDidUpdate() {
        this.reportPickerJobFinish();
    }

    /**
     * R14-SH16 (R14Q-56 ruling (a), picker H 89cff12b2): the picker's duplicate-Convert guard holds from the press until the
     * job is marked done / failed (or its 10 min cap), and the picker has no done / failed source of its own -- THIS card has
     * the per-workflow verdict. When the card reaches a terminal state (`pickerCardVerdict`) and a picker record names its
     * workflow, persistence's `recordJobFinish` is written once per card per verdict: `pickerVerdictSent` is the instance
     * guard, set BEFORE the write so neither a re-render nor a throwing store can repeat it; a verdict that has no record
     * behind it (an ordinary card, a swept record) is remembered too, so the storage scan is not paid on every update. A
     * Studio composition is never a picker job and is skipped before any read. Never throws.
     */
    private pickerVerdictSent: 'done' | 'failed' | null = null;
    reportPickerJobFinish(): void {
        try {
            const fu = this.props.fileUpload;
            if (!fu || isStudioWorkflow(fu)) return;
            const verdict = pickerCardVerdict(fu);
            if (!verdict || verdict === this.pickerVerdictSent) return;
            this.pickerVerdictSent = verdict;
            recordPickerJobFinish(fu.workflowId, verdict, this.storageService);
        } catch { /* the guard's cap is the fallback; the card renders regardless */ }
    }

    /** Teardown handle for the schedule-state subscription. */
    private unsubscribeSchedule: (() => void) | null = null;

    /** Same guard as the capability probe: a card removed before the flag lands must not update. */
    private applyPdfEditorEnabled(enabled: boolean) {
        if (!this.mounted || enabled === this.pdfEditorEnabled) return;
        this.pdfEditorEnabled = enabled;
        this.forceUpdate();
    }

    /** The capability probe resolves off-thread; a card removed before then must not update. */
    private applyEditorSafe(safe: boolean) {
        if (!this.mounted || safe === this.editorSafe) return;
        this.editorSafe = safe;
        this.forceUpdate();
    }

    // Also the teardown point for the feature-flag subscription: `untilDestroyed` wraps this
    // method when it is first called, so it must stay declared.
    componentWillUnmount() {
        if (this.neverStartedTimer) clearTimeout(this.neverStartedTimer);
        this.neverStartedTimer = null;
        this.mounted = false;
        if (BrowserUtils.hasWindow()) {
            window.removeEventListener('pageshow', this.resetChainState);
            window.removeEventListener('visibilitychange', this.resetChainState);
        }
        if (this.unsubscribeSchedule) {
            this.unsubscribeSchedule();
            this.unsubscribeSchedule = null;
        }
    }

    /**
     * Editor re-open T3 (EDITOR_REOPEN_PLAN.md §4, R-A / R-E): where this card's editor key goes -- the Studio, the picker at
     * its record, the expired state, or (Quick convert only) the generic modal. Pure over the card and this browser's storage.
     */
    reeditRoute(fileUpload: FileUpload): ReeditRoute {
        return reeditRouteOf(fileUpload, this.storageService, {
            isStudio: (fu) => isStudioWorkflow(fu),
            studioOpen: (fu) => studioProjectStillOpen(fu) && studioReeditHref(fu) !== null,
            studioRehydrateHref: (fu) => this.studioRehydrateHrefOf(fu as FileUpload),
        });
    }

    /**
     * Review 7 S3-05: a Studio card whose project this browser no longer holds goes back to the Studio, which rebuilds the
     * project from the workflow record (`?rwf=&rdom=`, components/studio/state/rehydrate.ts, `c4e92132b`) -- while its files
     * can still be on the edge. Past the 2.5 h lifetime (`STUDIO_TTL_MS`, measured from the job's end) the card says the
     * files are gone instead. A card with no known end time is tried: the Studio says honestly if the files are gone.
     */
    studioRehydrateHrefOf(fileUpload: FileUpload, now: number = Date.now()): string | null {
        const composition = studioCompositionOf(fileUpload);
        const projectId = typeof composition?.projectId === 'string' ? composition.projectId : '';
        const slug = cardSlugOf(fileUpload);
        const pm = fileUpload?.endpointData?.processorMetadataList?.[0];
        const domain = pm?.processDomain || pm?.uploadDomain || '';
        const ended = [fileUpload?.processEndTime, (fileUpload?.result as any)?.end, fileUpload?.processStartTime]
            .map(t => (t ? new Date(t as any).getTime() : NaN))
            .find(t => Number.isFinite(t));
        if (ended !== undefined && now - ended > STUDIO_TTL_MS) return null;
        return slug ? rehydrateStudioHref(slug, projectId, String(fileUpload?.id ?? ''), String(domain)) : null;
    }

    async reedit(fileUpload: FileUpload) {
        // §11.0 R11: a Studio workflow re-edits in the Studio. The merge modal loads options by
        // `uniqueProcessType` (file-upload-reedit-modal.component.tsx:63-64) and would hand a
        // composition to an editor that has no idea what it is. Same predicate as the
        // FILE_DOWNLOAD `source` attribute below, so the two cannot disagree.
        // Editor re-open T3: the picker likewise re-opens in the picker, and a Studio or picker job with nothing
        // left to re-open says so (R-E) -- never the generic modal, which is for Quick convert only (R-A).
        const route = this.reeditRoute(fileUpload);
        if (route.kind === 'studio') {
            if (route.href) {
                try {
                    await Router.push(route.href);
                } catch {
                    if (typeof window !== 'undefined') window.location.assign(route.href);
                }
                return;
            }
            if (openStudioReedit(fileUpload)) return;
            // No router yet: the href itself (never the modal for a Studio job).
            const href = studioReeditHref(fileUpload);
            if (href && typeof window !== 'undefined') window.location.assign(href);
            return;
        }
        if (route.kind === 'picker') {
            try {
                await Router.push(route.href);
            } catch {
                if (typeof window !== 'undefined') window.location.assign(route.href);
            }
            return;
        }
        if (route.kind === 'expired') {
            this.setState({ reeditExpired: route.editor });
            return;
        }
        await this.initializeScriptsAndWebsockets();
        const initialState = {
            fileUpload,
        };
        Modal.createModal(FileUploadReEditModalComponent, initialState);
    }

    /**
     * BUG-08 (manual-qa/review6/studio/review-notes.md): returning to the Studio after an
     * export silently CONTINUES that finished project -- `studio-tab.component.tsx`'s
     * `restore()` falls back to `readLatest(pageSlug)` whenever the URL it lands on carries no
     * `sid`, so "Back to Studio" (and any bare link to the tab) hands the user the project they
     * just exported. Proven at frame level: a second export's opening seconds were the first
     * export's photos.
     *
     * The Studio's own answer is Start Over, but that control lives inside the editor with
     * nothing on THIS page (`/downloads`) to wire it to. It does not need wiring: `restore()`
     * already opens a brand-new, empty document whenever the `sid` it is handed does not
     * resolve to a stored record -- the same "unknown/newer record version -> absent" branch a
     * stale or foreign `sid` takes. A freshly minted id has no record by construction, so
     * handing the Studio one reaches the identical empty-project outcome Start Over produces,
     * through a path that already exists and is left exactly as it was.
     */
    makeAnotherVideoHref(fileUpload: FileUpload): string | null {
        if (!isStudioWorkflow(fileUpload)) return null;
        const slug = fileUpload?.pageData?.path || fileUpload?.uniqueProcessType;
        if (!slug) return null;
        // Review 7 S3-01: `src=new` -- the Studio opens an empty project with no "couldn't find that project" line.
        return newStudioHref(slug, newProjectId());
    }

    /** Same non-anchor navigation `reedit()` below and `openStudioReedit` use: Next's router. */
    private makeAnotherVideo = (fileUpload: FileUpload) => {
        const href = this.makeAnotherVideoHref(fileUpload);
        if (!href) return;
        try {
            Router.push(href);
        } catch { /* no router instance yet -- there is nothing else for a plain button to do */ }
    };

    private async initializeScriptsAndWebsockets() {
        await this.spinner.runWithLoader(async () => {
            try {
                await this.scriptLoadService.loadAllScriptsIfNotLoaded();
            } catch (e) {
                this.logger.error({ message: 'Something went wrong while loading scripts.', error: e });
            }
        }, 'Please wait while we initialize file upload service...');
    }

    hasDirectDownloadLink(fileUpload: FileUpload): boolean {
        if (!this.isFileReady(fileUpload) || fileUpload.result.error === true) {
            return false;
        }

        if (Array.isArray(fileUpload.result.output)) {
            return false;
        }
        return true;
    }

    getRangeSupportedDirectDownloadLink(fileUpload: FileUpload, preview = false): string {
        if (!this.hasDirectDownloadLink(fileUpload)) {
            return null;
        }
        if (Array.isArray(fileUpload.result.output)) {
            return null;
        }

        const tokens = fileUpload.result.output.split('\\');
        const link = this.fileDownloadService.getRangeSupportedDirectDownloadLink(fileUpload, tokens[tokens.length - 1], preview);
        setTimeout(() => {
            gapi.savetodrive.go(`id${this.props.fileUpload.id}`);
        }, 100);
        return link;
    }

    async onDirectDownloadBtnHandler(fileInfo: FileUpload) {
        await this.scriptLoadService.loadAllScriptsIfNotLoaded();
        if (!this.isFileReady(fileInfo)) {
            this.uiContext.warnToast('Processing File..', 'We are processing your file. Please wait...', true, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast(
                'Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`
                , true,
                true);
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                const initialState = {
                    title: 'Download individual files',
                    files: fileInfo.result.output,
                    name: fileInfo.name,
                    numberOfFiles: fileInfo.result.output.length,
                    class: 'gray modal-lg',
                    fileInfo: fileInfo,
                    displayNames: this.pickerDisplayNames(fileInfo),
                };
                Modal.createModal(SingleFileDownloadModalComponent, initialState);
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory.BUTTON,
                    GAContants.EventAction['MODEL-OPEN'],
                    GAContants.EventLabel.NAVIGATE,
                    fileInfo.name,
                );
            } else {
                const tokens = fileInfo.result.output.split('\\');
                const link = this.fileDownloadService.getDirectDownloadLink(fileInfo, tokens[tokens.length - 1]);
                this.uiContext.showSuccessDialog("Download Links", null, `
                    <div style="text-align: left; word-break: break-all;">
                        <div>
                            Filename: 
                            <div class="alert alert-info">${escapeHtml(fileInfo.name)}</div>
                        </div>
                        <div>
                            Link:
                            <div class="alert alert-info" style="">${escapeHtml(link)}</div>
                        </div>
                        <div>*To download file directly, copy and paste above link to your browser's address bar.</div>
                    </div>
                `);
            }
        }
    }


    /**
     * Where this card's file came from, for `FILE_DOWNLOAD{source}` (§8, §12).
     *
     * `studio` by the predicate Re-edit uses (`isStudioWorkflow`), so "downloaded from the
     * Studio" and "re-opens in the Studio" cannot disagree; `picker` by the processing note the
     * badge, the ZIP primary and the link read, for the same reason. A Studio composition and a
     * picker note cannot both be on one card (different pages, different strategies); the
     * Studio is asked first only so the pre-existing branch is byte-for-byte what it was.
     */
    private downloadSource(fileInfo: FileUpload): 'studio' | 'picker' | 'convert' {
        if (isStudioWorkflow(fileInfo)) return 'studio';
        // Editor re-open T3: the same predicate as the editor key and Re-edit's destination (a queued picker card with a record counts).
        if (isFramePickerWorkflow(fileInfo, this.storageService)) return 'picker';
        return 'convert';
    }

    /**
     * ONE emit for both download buttons (§8: `FILE_DOWNLOAD{zip: true}` from the ZIP handler
     * too — F9/B3). Before this the ZIP path counted nothing, and for a picker sequence the ZIP
     * IS the download, so the sequence SLO (§8, ≥ 70 % counting the ZIP) had no numerator.
     */
    private emitFileDownload(fileInfo: FileUpload, zip: boolean) {
        // Session-context download signal (sweep P2): the server flag says WHAT was
        // downloaded; this says when/where in the session it happened.
        const metrics = container.get(MetricsService);
        metrics.downloadedThisSession = true;
        metrics.emit(MetricEventName.FILE_DOWNLOAD, {
            // B3: FileUpload.workflowId is persisted at schedule time since 32dd0889.
            workflowId: fileInfo.workflowId ?? '',
            page: typeof location !== 'undefined' ? location.pathname : '',
            // §8. Derived from the card itself, by the same predicates that decide where Re-edit
            // goes and how the card renders, so the attribute and the card cannot disagree.
            source: this.downloadSource(fileInfo),
            zip,
        });
    }

    onDownloadBtnHandler(fileInfo: FileUpload) {
        this.emitFileDownload(fileInfo, false);
        if (!this.isFileReady(fileInfo)) {
            this.uiContext.warnToast('Processing File..', 'We are processing your file. Please wait...', true, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast(
                'Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`
                , true,
                true);
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                const initialState = {
                    title: 'Download individual files',
                    files: fileInfo.result.output,
                    name: fileInfo.name,
                    numberOfFiles: fileInfo.result.output.length,
                    class: 'gray modal-lg',
                    fileInfo: fileInfo,
                    displayNames: this.pickerDisplayNames(fileInfo),
                };
                Modal.createModal(SingleFileDownloadModalComponent, initialState);
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory.BUTTON,
                    GAContants.EventAction['MODEL-OPEN'],
                    GAContants.EventLabel.NAVIGATE,
                    fileInfo.name,
                );
            } else {
                const tokens = fileInfo.result.output.split('\\');
                this.fileDownloadService.downloadFile(fileInfo, tokens[tokens.length - 1]);
            }
        }
    }

    /**
     * Review 7 K7: ONE download per click, and always inside the click. A ZIP this page knows is built downloads synchronously
     * (no await in front of `window.open`). An unbuilt one is built first; if the build answers while the click's activation
     * still holds the download starts then, otherwise the key says the ZIP is ready and the NEXT click -- a fresh gesture --
     * downloads it without rebuilding. Never a second build while one runs, never a gesture-less download.
     */
    onZipDownloadBtnHandler(fileInfo: FileUpload): void {
        // The click that collects a ZIP an earlier click built completes that click's download: one FILE_DOWNLOAD{zip}, not two.
        if (!this.state.zipAwaitingClick) this.emitFileDownload(fileInfo, true);
        if (!fileInfo.result) {
            this.uiContext.warnToast('Processing File',
                'We are processing your file. Please wait...', false, true);
            return;
        }
        if (fileInfo.result.error === true) {
            this.uiContext.errorToast('Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`, true, true);
            return;
        }
        if (this.isZipFileReady || this.state.zipBuilt) {
            if (this.state.zipAwaitingClick) this.setState({ zipAwaitingClick: false });
            this.openZippedFileDownloadWindow(fileInfo);
            return;
        }
        if (this.state.zipDownloadInProgress) return;
        if (!this.canRequestZipFile) {
            this.uiContext.warnToast('Processing File',
                'We are processing your file. Please wait...', false, true);
            return;
        }
        void this.buildZipThenDownload(fileInfo);
    }

    private async buildZipThenDownload(fileInfo: FileUpload): Promise<void> {
        this.setState({ zipDownloadInProgress: true });
        await this.initializeScriptsAndWebsockets();
        await this.spinner.runWithLoader(async () => {
            try {
                await this.fileProcessService.createZip(fileInfo);
            } catch (err) {
                this.logger.error(err);
                this.setState({ zipDownloadInProgress: false });
                this.uiContext.errorToast('Could not make your ZIP',
                    `Please try again. If it keeps failing, let us know at ${this.envService.env.siteSupportEmail}`, true, false);
                return;
            }
            if (hasTransientActivation()) {
                this.setState({ zipDownloadInProgress: false, zipBuilt: true });
                this.openZippedFileDownloadWindow(fileInfo);
                return;
            }
            this.setState({ zipDownloadInProgress: false, zipBuilt: true, zipAwaitingClick: true });
            this.uiContext.successToast('Your ZIP is ready', 'Click the ZIP button to save it.');
        }, 'Zipping files for downloading..');
    }

    private openZippedFileDownloadWindow(fileInfo) {
        const tokens = fileInfo.result.zip.split('\\');
        const downloadWindowOpened = this.fileDownloadService.downloadFile(fileInfo, tokens[tokens.length - 1]);
        if (!downloadWindowOpened) {
            this.uiContext.warnToast('Pop-up blocked',
                'If file upload failed. Please make sure your browser not blocking pop-ups...', false, true);
        }
        return;
    }

    getDownloadURL(fileInfo: FileUpload) {
        if (!this.isFileReady(fileInfo)) {
        } else if (fileInfo.result.error === true) {
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                return 'modal';
            } else {
                try {
                    const tokens = fileInfo.result.output.split('\\');
                    return this.fileDownloadService.getDownloadURL(fileInfo, tokens[tokens.length - 1]);
                } catch (e) {
                    this.logger.error({ message: `Failed to decode result.output`, error: e, data: FileUpload.toLoggableObject(fileInfo) });
                    return;
                }
            }
        }
    }

    getOutputInfo(fileUpload: FileUpload): JSX.Element | string {
        if (!this.fileOutputInfo) {
            this.fileOutputInfo = this._getOutputInfo(fileUpload);
        }
        return this.fileOutputInfo;
    }

    private _getOutputInfo(fileUpload: FileUpload) {
        if (fileUpload.processType === ProcessTypes.COMPRESS) {
            const key = Object.keys(fileUpload.result.sizes.outputs);
            const outputSize = parseFloat(fileUpload.result.sizes.outputs[key[0]]);
            const ratio = parseFloat(1 * (100 * (1 - outputSize / fileUpload.size)) + '').toFixed(2);
            const ratio2 = parseFloat(1 * (100 * (outputSize / fileUpload.size)) + '').toFixed(2);

            return <div>
                {/* <div>Original size: {this.getFileSize(fileUpload.size)}</div> */}
                <div>New size:  {this.getFileSizeInfo(fileUpload)} ({ratio2}% of the original {this.getFileSize(fileUpload.size)})</div>
            </div>;
        }
        return `Output Size: ${this.getFileSizeInfo(fileUpload)}`;
    }

    private getFileSizeInfo(fileUpload: FileUpload) {
        if (fileUpload.processType !== ProcessTypes.SPLIT) {
            try {
                const key = Object.keys(fileUpload.result.sizes.outputs);
                return this.getFileSize(fileUpload.result.sizes.outputs[key[0]]);
            } catch (e) {
                this.logger.error({ message: e.message, error: e });
            }
        }
        return '';
    }

    private getFileSize(size) {
        // Decimal (SI), matching the tool-page file card and the backend's size targets.
        // This used to be filesize(size, { base: 2, standard: 'jedec' }) -- binary maths
        // wearing decimal labels -- so one 590,213-byte file read "590.21 KB" on the tool
        // page and "576.38 KB" here. On a site whose whole job is file size, the number
        // changing between screens reads as a bug in the conversion.
        return toReadableBytes(size, 'quantity');
    }

    isSingleFile(outputs: any) {
        return Object.keys(outputs).length === 1;
    }

    decodeDataType(fileUpload: FileUpload, dataType: string, data: string) {
        const wsData: WebsocketResponse = fileUpload.ws;
        if (dataType === 'enforcement_error') {
            let parsed: Record<string, unknown> = {};
            try {
                parsed = JSON.parse(data);
            } catch { /* fall through to fallback copy */ }
            // Surface in the global modal as well so the user gets the same
            // structured rejection UI regardless of which code path (pre-upload
            // resolve 429 vs post-upload WS) triggered the block.
            try {
                this.enforcementErrorService?.show(
                    EnforcementErrorService.fromBody(parsed as never, 429),
                );
            } catch { /* best-effort — never block the inline message */ }
            return (parsed as { userMessage?: string }).userMessage
                || 'Usage limit reached. Please try again later.';
        }
        if (dataType === 'notification') {
            if (wsData.processStage == ProcessStageTypes.UPLOAD) {
                // Server-to-server transfer progress. CONTRACT with ironman
                // downloadUploadedFilesToLocalDisk: `data` is the raw cumulative byte
                // count as a decimal string. Anything else (legacy "Retrieved 12.34 MB",
                // "File retrieval started/completed.") must fall back to plain copy —
                // lodash toInteger() turned those into 0, so the card showed a frozen
                // "0 Bytes/<total>" for the entire transfer.
                const totalFileSize = SafeExecUtils.exec(() => fileUpload.originalInputFiles.map(f => f.metadata.fsize).reduce((p, c) => p + c, 0), null, -1);
                const dataVal = /^\d+$/.test(String(data).trim()) ? toInteger(data) : NaN;
                const totalOk = totalFileSize !== -1 && isNumber(totalFileSize) && !isNaN(totalFileSize);
                if (wsData.processStageSubState == ProcessStageSubStateTypes.COMPLETED) {
                    if (totalOk && !isNaN(dataVal) && dataVal > 0) {
                        return `Sending file to worker ${this.getFileSize(dataVal)}/${this.getFileSize(totalFileSize)}`;
                    }
                    return 'File sent to worker';
                } else if (wsData.processStageSubState == ProcessStageSubStateTypes.PARTIAL) {
                    if (totalOk && !isNaN(dataVal)) {
                        return `Sending file to worker ${this.getFileSize(dataVal)}/${this.getFileSize(totalFileSize)}`;
                    }
                }
                return 'Sending file to worker';
            } else if (wsData.processStage == ProcessStageTypes.PREPROCESS || wsData.processStage == ProcessStageTypes.PROCESSING) {
                return data;
            }
            if (wsData.processStage == ProcessStageTypes.COMPLETED && data.toLocaleLowerCase() === 'completed') {
                const startTimeInMilli = PSDate.from(fileUpload.uploadStartTime).getTime();
                if (fileUpload.processEndTime) {
                    const endTimeInMilli = PSDate.from(fileUpload.processEndTime).getTime();
                    const prettyTimeMs = new PSRedableDuration(PSDate.from(fileUpload.uploadStartTime))
                        .fromDate(PSDate.from(fileUpload.processEndTime));
                    return `${data}: Took ${prettyTimeMs}`;
                }
                const currentTimeInMilli = PSDate.from().toDate().getTime();
                const prettyTime = new PSRedableDuration(PSDate.from().subtract(currentTimeInMilli - startTimeInMilli, 'ms'))
                    .fromNow();
                return `${data} ${prettyTime}`;
            }
            return data || 'Server Processing File';
        } else if (dataType === 'ratio') {
            return 'Server Processing File';
        } else if (dataType === 'percentage') {
            const message = get(fileUpload, 'ws.readableProcessStage') || 'Server Processing File';
            return message;
        } else {
            return 'Waiting for server to reply';
        }
    }

    getProcessInfo(fileUpload: FileUpload): string {
        if (!this.fileProcessInfo) {
            this.fileProcessInfo = this._getProcessInfo(fileUpload);
        }
        return this.fileProcessInfo;
    }

    private _getProcessInfo(fileUpload: FileUpload): string {
        // S-43: a Studio export describes itself ("Studio video · 4 photos") — the generic sentence
        // below would read the OUTPUT's extension twice ("Converted MP4 to MP4"). Same pattern as
        // the picker's `pick-another-frame` branch: the feature module owns the line.
        const studioLine = studioCardLine(fileUpload);
        if (studioLine) return studioLine;
        const inputExtension = getExtension(fileUpload.name);
        if (fileUpload.processType === ProcessTypes.SPLIT) {
            const splitOptions = ConvertOptionsUtils.getPropertyValue(fileUpload, 'split');
            if (splitOptions && splitOptions.formValue === 'page_range') {
                return `Extracted PDF ${splitOptions.formChildren[0].formValue} to ${splitOptions.formChildren[1].formValue}`;
            }
            if (fileUpload.fromType.includes('archive')) {
                return `Extracted to individual files`;
            }
            return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}ted PDF to individual pages`;
        } else if (fileUpload.processType === ProcessTypes.COMPRESS) {
            const outputQualityValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'outputQuality')
                || ConvertOptionsUtils.getPropertyValue(fileUpload, 'outputQualityAndSize');

            const crfValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'crf')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'crf');
            const presetValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'preset')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'preset');
            const qminValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'qmin')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'qmin');
            const qmaxValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'qmax')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'qmax');

            const minrate = ConvertOptionsUtils.getPropertyValue(fileUpload, 'minrate')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'minrate');
            const maxrate = ConvertOptionsUtils.getPropertyValue(fileUpload, 'minrate')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'minrate');
            if (outputQualityValue && isString(outputQualityValue)) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                            ${inputExtension.toLocaleUpperCase()} with quality
                            ${outputQualityValue === '-1' ? 'unchanged' : outputQualityValue + '%'}`;
            } else if (crfValue && presetValue) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                             ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and Preset ${presetValue}`;
            } else if (crfValue && qminValue && qmaxValue) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                            ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                            Quality ${qminValue}-${qmaxValue}%`;
            } else if (crfValue && minrate || minrate) {
                if (minrate &&
                    minrate) {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${minrate} (minrate) - ${maxrate} (maxrate)`;
                } else if (maxrate) {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${maxrate} (maxrate)`;
                } else {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${minrate} (minrate)`;
                }

            } else {
                const processInfo = `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))} ${inputExtension.toLocaleUpperCase()}`;
                return processInfo;
            }
        } else if (fileUpload.processType === ProcessTypes.MERGE) {
            return `${StringUtils.capitalizeFirstLetter(
                this.getProcessTypePastTense(fileUpload.processType))} ${fileUpload.fromType[0].toUpperCase()} files to ${fileUpload.toType.toUpperCase()}`;
        }
        else if (fileUpload.processType === ProcessTypes.EXTRACT) {
            return `${StringUtils.capitalizeFirstLetter(`Extracted ${fileUpload.extension[0].toUpperCase()}`)}`;
        }
        else if (fileUpload.processType === ProcessTypes.ARCHIVE) {
            return `${StringUtils.capitalizeFirstLetter(`Archived files to ${fileUpload.toType.toUpperCase()}`)}`;
        }

        const toExtensionOverride = ConvertOptionsV2Utils.getPropertyValue(fileUpload, 'toExtensionOverride');
        let toExtension;
        if (toExtensionOverride && toExtensionOverride !== 'SAME_AS_SOURCE') {
            toExtension = toExtensionOverride;
        } else if (toExtensionOverride === 'SAME_AS_SOURCE') {
            toExtension = inputExtension; // use input file's extension
        } else {
            toExtension = ConvertOptionsV2Utils.getPropertyValue(fileUpload, 'toExtension');
        }
        let extension = toExtension ? getExtension(toExtension) : undefined;
        try {
            if (extension === 'original') {
                extension = getExtension(fileUpload.originalInputFiles[0].originalName);
            }
        } catch (e) { }
        const processInfo = `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
             ${inputExtension.toLocaleUpperCase()} to ${extension?.toLocaleUpperCase()}`;
        return processInfo;
    }

    getProcessTypePastTense(pType: string) {
        if (/resize$/i.test(pType)) {
            return 'resized';
        } else if (/merge$/i.test(pType)) {
            return 'merged';
        } else if (/extract$/i.test(pType)) {
            return 'extracted';
        } else if (/convert$/i.test(pType)) {
            return 'converted';
        } else if (/rotate$/i.test(pType)) {
            return 'rotated';
        } else if (/trim$/i.test(pType)) {
            return 'trimmed';
        } else if (/crop$/i.test(pType)) {
            return 'cropped';
        } else if (/compress$/i.test(pType)) {
            return 'compressed';
        } else {
            return pType;
        }
    }

    decodeData(fileUpload: FileUpload) {
        const wsData: WebsocketResponse = fileUpload.ws;
        if (wsData.dataType === 'percentage') {
            if (wsData.processStage === 'processing') {
                if (wsData.data.match(/^completed$/i)) {
                    return '100';
                }
                const val = parseFloat(wsData.data).toFixed(2);
                return val;
            }
        } else if (wsData.dataType === 'notification') {
            if (wsData.processStage === 'upload') {
                if (wsData.processStageSubState.match(/^completed$/i)) {
                    return '100';
                }
                const val = Number(100 * parseFloat(wsData.data) / fileUpload.size).toFixed(2);
                return val;
            }
            return wsData.data;
        } else if (wsData.dataType === 'ratio') {
            return wsData.data;
        } else {
            return 'Waiting for server to reply';
        }
    }

    isFileReady(fileInfo: FileUpload) {
        if (!fileInfo.result || Object.keys(fileInfo.result).length === 0) {
            return false;
        }
        return true;
    }

    checkStatus(fileInfo: FileUpload) {
        return this.processCompleteCheckService.requestToSendFileCompleteDataThroughWS(
            fileInfo.endpointData.processorMetadataList[0].processDomain,
            fileInfo.id,
        ).then(data => this.logger.trace7(data))
            .catch(err => this.logger.error({ error: err, message: `Error while checking status ${err.message}` }));
    }

    async checkStatusAndShowMessage(fileInfo: FileUpload) {
        try {
            await this.initializeScriptsAndWebsockets();
            this.processCompleteCheckService.requestToSendFileCompleteDataThroughWS(
                fileInfo.endpointData.processorMetadataList[0].processDomain,
                fileInfo.id,
            ).then(stateRespose => {
                if (stateRespose.state === 'success') {
                    this.uiContext.successToast(
                        'Process Success',
                        'Process successfully completed. Please refresh page.',
                    );
                } else if (stateRespose.state === 'error') {
                    this.uiContext.errorToast(
                        'Error',
                        'Process failed.',
                        false,
                        false,
                    );
                } else {
                    this.uiContext.warnToast(
                        'Unknown',
                        'It\'s looks like server still processing file.',
                        false,
                        false,
                    );
                }
            }).catch(err => {
                this.uiContext.handleError(err);
            }).finally(() => {
                this.forceUpdate();
            });
        } catch (e) {
            this.uiContext.handleError(e);
        }
    }

    getFileDownloadBtnText(fileUpload: FileUpload) {
        const numberOfOutputFiles = get(fileUpload, 'result.outputFiles', []).length;
        if (numberOfOutputFiles > 1) {
            return `Show Downloads (${numberOfOutputFiles} files)`
        }
        return 'Download';
    }

    getOutputFileNameIfAvailable(fileUpload: FileUpload) {
        // S-43: the Studio pins one container; its suffix is lower-case `.mp4`, not the option pin `MP4`.
        const studioName = studioCardFileName(fileUpload);
        if (studioName) return studioName;
        // R14-SH12 (R14U-29): a picker SEQUENCE job downloads ONE zip, `<stem>-frames.zip` (ironman c8653a59 /
        // FramePickerNaming); the card used to title it `<stem>.JPG` -- the entries' format, not the file you get.
        const pickerRange = pickerJobRangeForWorkflow(fileUpload.workflowId, this.storageService);
        if (pickerRange?.mode === 'SEQUENCE' || framePickerWorkflowNoteOf(fileUpload, this.storageService)?.mode === 'PICKED') { // T5: the pinned frames' job zips the same way
            const dot = fileUpload.name.lastIndexOf('.');
            return `${dot > 0 ? fileUpload.name.slice(0, dot) : fileUpload.name}-frames.zip`;
        }
        let filename = fileUpload.name;
        const override = fileUpload.options?.toExtensionOverride;
        let toExtension;
        if (override && override !== 'SAME_AS_SOURCE') {
            toExtension = override;
        } else if (override === 'SAME_AS_SOURCE') {
            toExtension = getExtension(fileUpload.name);
        } else {
            toExtension = fileUpload.options?.toExtension ?? getExtension(fileUpload.originalInputFiles[0].originalName);
            if (toExtension === 'SAME_AS_SOURCE') {
                toExtension = getExtension(fileUpload.name);
            }
        }
        filename = replaceExtension(fileUpload.name, toExtension);
        return filename;
    }

    async removeFile(fileUpload: FileUpload) {
        await this.initializeScriptsAndWebsockets();
        this.uiContext.showConfirmationWarningDialog(
            'Delete File',
            'Are you sure you want to delete?',
            async () => {
                await this.fileManagerService.removeFile(fileUpload);
            },
        );
    }

    /**
     * Show the user their result.
     *
     * Two sources are always in play: the real file, and a server-rendered still of it. The
     * capability check only decides which to *start* with — no table is right on every browser,
     * OS and codec combination, so the modal attempts the real file and falls back to the still
     * on a genuine decode failure. Being wrong optimistically costs one request; being wrong
     * pessimistically costs the real preview. Both recover, and neither dead-ends the user,
     * which the old "download the file to view it" toast did on the exact control they clicked
     * to find out whether the conversion worked.
     */
    openPreview(fileUpload: FileUpload) {
        const kind = previewKindFor(fileUpload.toType);
        if (!kind) return;
        container.get(MetricsService).emit(MetricEventName.PREVIEW_OPENED, {
            workflowId: String((fileUpload as any).workflowId ?? (fileUpload as any).uuid ?? ''),
            kind,
        });
        if (!this.isFileReady(fileUpload)) {
            this.uiContext.errorToast('Not ready yet', 'This file is still converting.', false, false);
            return;
        }
        canPreviewOutput(fileUpload.toType, (fileUpload as any).result?.videoCodec)
            .then(verdict => this.showPreview(fileUpload, kind, verdict))
            // An unreachable capability check is not a reason to refuse: attempt it.
            .catch(() => this.showPreview(fileUpload, kind, 'unknown'));
    }

    private showPreview(fileUpload: FileUpload, kind: PreviewKind, verdict: CapabilityResult) {
        // Review 7 K7 (a)/(b): a many-file result (`result.output` is an ARRAY -- a picker sequence, the pinned frames, a
        // split) has no single file, so every URL below answered null: the modal drew `<img src=null>` (no request at all)
        // and its download key opened `/null`. It gets its own path.
        if (Array.isArray(fileUpload?.result?.output)) {
            this.showManyFilePreview(fileUpload, kind, fileUpload.result.output as unknown as string[]);
            return;
        }
        // Audio has no server-rendered still to fall back to (the thumb endpoint 404s),
        // so never offer one: a failed audio preview should say so, not chase a poster.
        const poster = kind === 'audio' ? null : this.outputPosterUrl(fileUpload);
        const format = (fileUpload.toType || 'this format').toUpperCase();
        const fallbackNotice = `Your browser can't open ${format} directly, so this is a still `
            + 'image of the converted result. Download the file to use it.';

        const realUrl = this.getRangeSupportedDirectDownloadLink(fileUpload, true);
        // The modal's Download button must take the SAME route as the card's Download button:
        // getDirectDownloadLink → ironman's /v2/edge/downloads/<file>, which records
        // workflow.downloaded. The /downloads/<file> "view" path the preview streams from is
        // served by nginx straight off the disk (auth_request only) and records nothing — so
        // passing the preview URL here (until 2026-09-06) made every save from the modal
        // invisible, and merely stripping `&preview=true` would have changed nothing.
        const outputName = (!fileUpload?.result?.output || Array.isArray(fileUpload.result.output))
            ? null : fileUpload.result.output.split('\\').pop();
        const countedUrl = outputName ? this.fileDownloadService.getDirectDownloadLink(fileUpload, outputName) : realUrl;

        // 'no' means this browser definitely cannot decode it, so go straight to the poster
        // rather than spending the user's bandwidth on bytes that will not render. Download
        // still points at the real file.
        if (verdict === 'no' && poster) {
            MediaPreviewModalComponent.show({
                url: poster,
                downloadUrl: countedUrl,
                mediaType: 'image' as const,
                filename: this.previewTitleOf(fileUpload),
                notice: fallbackNotice,
            });
            return;
        }

        // 'yes' and 'unknown' both attempt the real file; the modal swaps to the poster if it
        // genuinely fails to render, which is what makes a wrong capability answer survivable.
        MediaPreviewModalComponent.show({
            url: realUrl,
            downloadUrl: countedUrl,
            mediaType: kind === 'video' ? 'video' as const
                : kind === 'audio' ? 'audio' as const
                    : fileUpload.toType === 'pdf' ? 'pdf' as const
                        : 'image' as const,
            filename: this.previewTitleOf(fileUpload),
            fallbackUrl: poster || undefined,
            fallbackNotice: poster ? fallbackNotice : undefined,
        });
    }

    /**
     * Review 7 K7: the preview of a many-file card. Image outputs (frames) open as a gallery of the real files, the first on
     * screen, one request per frame viewed (the preview path, `preview=true`, so it records no download). Anything else shows
     * its first file. The modal's download key does what the card's own primary does -- the ZIP where the card has one, else
     * the "Download individual files" list -- because no single URL is "the file" here.
     */
    private showManyFilePreview(fileUpload: FileUpload, kind: PreviewKind, outputs: string[]) {
        const names = outputs.map(o => String(o ?? '').split('\\').pop()).filter(Boolean);
        if (!names.length) {
            this.uiContext.errorToast('Nothing to preview', 'This result has no files to show.', false, false);
            return;
        }
        const urls = names.map(n => this.fileDownloadService.getRangeSupportedDirectDownloadLink(fileUpload, n, true));
        const zip = !!this.zipSupported && !!fileUpload.result?.zip;
        const mediaType = kind === 'video' ? 'video' as const
            : kind === 'audio' ? 'audio' as const
                : fileUpload.toType === 'pdf' ? 'pdf' as const
                    : 'image' as const;
        MediaPreviewModalComponent.show({
            url: urls[0],
            gallery: mediaType === 'image' && urls.length > 1 ? urls : undefined,
            mediaType,
            filename: this.previewTitleOf(fileUpload),
            onDownload: () => (zip ? this.onZipDownloadBtnHandler(fileUpload) : this.onDownloadBtnHandler(fileUpload)),
            downloadLabel: zip ? this.getZipDownloadBtnText(fileUpload) : this.getFileDownloadBtnText(fileUpload),
        });
    }

    /**
     * Review 7 M1-03: until the result lands, hold the space its lines will take ("Converted MOV to JPG", "Output Size: …"),
     * so the footer -- the Download key the user's thumb is waiting on -- does not drop 45 px the instant it turns live.
     * One line for a many-file job (no per-file size line), two otherwise; nothing on a failed card or once a result is in.
     */
    getResultLinesReserve(fileUpload: FileUpload): JSX.Element | null {
        if (!fileUpload || this.hasFailed(fileUpload) || this.neverStarted(fileUpload)) return null;
        const result: any = fileUpload.result;
        if (result && (result.sizes?.outputs || result.output)) return null;
        const lines = this.zipSupported ? 1 : 2;
        return <div className={styles['result-lines-reserve']} data-lines={lines} data-jkey="file-list-item-result-reserve" aria-hidden="true" />;
    }

    private neverStartedTimer: ReturnType<typeof setTimeout> | null = null;

    /** Review 7 S3-02: nothing -- server, edge, coordinator or upload -- has ever reported on this card. */
    isUnstarted(fileUpload: FileUpload): boolean {
        if (!fileUpload || this.hasFailed(fileUpload)) return false;
        if (fileUpload.workflowId) return false;
        if (fileUpload.result && Object.keys(fileUpload.result).length > 0) return false;
        if (fileUpload.ws && fileUpload.ws.processStage) return false;
        if (scheduleCoordinator.enabled && scheduleCoordinator.has(fileUpload.id)) return false;
        // WEB-CORE-06: a schedule on its retry ladder is a started job, with the coordinator (clientQueuedState) off too.
        if (scheduleInFlight.has(fileUpload.id)) return false;
        const progress = Number(fileUpload.progress);
        if (progress > 0 && progress < 100) return false;
        return true;
    }

    /** Review 7 S3-02: start the bound once, when the card mounts unstarted. */
    armNeverStartedTimer() {
        if (this.neverStartedTimer || !this.isUnstarted(this.props.fileUpload)) return;
        this.neverStartedTimer = setTimeout(() => {
            this.neverStartedTimer = null;
            this.setState({ unstartedBoundPassed: true });
        }, NEVER_STARTED_AFTER_MS);
    }

    /** Review 7 S3-02: past the bound AND still unstarted -- a late start puts the card straight back to normal. */
    neverStarted(fileUpload: FileUpload): boolean {
        return !!this.state?.unstartedBoundPassed && this.isUnstarted(fileUpload);
    }

    /** Review 7 P2-07: a picker job's files listed by the names they SAVE as (the edge's FramePickerNaming); undefined otherwise. */
    private pickerDisplayNames(fileInfo: FileUpload): string[] | undefined {
        const landed = framePickerNoteOf(fileInfo?.result?.processingNotes);
        const outputs = fileInfo?.result?.output as unknown;
        if (!landed || !Array.isArray(outputs)) return undefined;
        return outputs.map(o => pickerOutputFileName(landed, fileInfo.name, String(o)) ?? String(o));
    }

    /**
     * Review 7 JB-R1-01: the settings echo under the card. A Studio export is always an H.264 MP4 (the Studio pins its
     * container, `STUDIO_CONTAINER_EXT`, and the sheet says "MP4 (H.264)"); its card options are the PAGE's (a WEBM page
     * carries `videoCodec: VP9`, `audioCodec: OPUS`), so echoing them described a file nobody got. A Studio card echoes the
     * pin; every other card echoes its own options, unchanged.
     */
    optionEchoOf(fileUpload: FileUpload): string | null {
        // WEB-CORE-07: the codec from the Studio's one statement of it (`output-format.ts`), never a literal here.
        if (isStudioWorkflow(fileUpload)) return formatOptionEcho({ toExtension: STUDIO_CONTAINER_EXT.toUpperCase(), videoCodec: STUDIO_VIDEO_CODEC });
        return formatOptionEcho(fileUpload?.options as any);
    }

    /** Review 7 K7: the modal is titled with what the card delivers (its own header name), not the source file; never throws. */
    private previewTitleOf(fileUpload: FileUpload): string {
        try {
            return this.getOutputFileNameIfAvailable(fileUpload) || fileUpload.name;
        } catch {
            return fileUpload.name;
        }
    }

    /** Server-rendered still of the output, or null when there is nothing to ask for. */
    private outputPosterUrl(fileUpload: FileUpload): string | null {
        const output = fileUpload?.result?.output;
        if (!output || Array.isArray(output)) return null;
        const tokens = output.split('\\');
        return this.fileDownloadService.getOutputThumbnailLink(fileUpload, tokens[tokens.length - 1]);
    }

    async saveToDropbox(filename: string, downloadLink: string) {
        const instant = await this.spinner.show('Uploading to Dropbox');
        var options = {
            files: [
                // You can specify up to 100 files.
                { 'url': downloadLink, 'filename': filename },
                // ...
            ],

            // Success is called once all files have been successfully added to the user's
            // Dropbox, although they may not have synced to the user's devices yet.
            success: () => {
                // Indicate to the user that the files have been saved.
                this.spinner.hide(instant);
            },

            // Progress is called periodically to update the application on the progress
            // of the user's downloads. The value passed to this callback is a float
            // between 0 and 1. The progress callback is guaranteed to be called at least
            // once with the value 1.
            progress: (progress) => {
                this.spinner.updateMessage(instant, `Uploading to Dropbox. ${progress * 100} completed.`);
                if (progress === 1) {
                    this.spinner.hide(instant);
                }
            },

            // Cancel is called if the user presses the Cancel button or closes the Saver.
            cancel: () => {
                this.spinner.hide(instant);
            },

            // Error is called in the event of an unexpected response from the server
            // hosting the files, such as not being able to find a file. This callback is
            // also called if there is an error on Dropbox or if the user is over quota.
            error: (errorMessage) => {
                this.spinner.hide(instant);
                this.uiContext.showErrorDialog({ title: 'Error', text: errorMessage });
            }
        };
        Dropbox.save(downloadLink, filename, options);
    }

    getFooterButtons(fileUpload) {
        // §6.5: for a picker sequence the ZIP is what the user asked for, so it is the primary
        // and "Show Downloads (23 files)" steps back to secondary. Every other card is untouched.
        const pickerSequence = this.isPickerSequence;
        // Round 10 (R10-M2, R9 QA N5): ONE name for the way back to the Studio. The /downloads lead row
        // above the list says "Back to Studio" (file-download.component.tsx `STUDIO_RETURN_COPY.cta`); a
        // Studio card's own key said "Re-edit" -- two ways back with two names. Same predicate as
        // `reedit()` (`isStudioWorkflow`), so the label and the destination cannot disagree.
        const studioCard = isStudioWorkflow(fileUpload);
        const reeditLabel = studioCard ? 'Back to Studio' : 'Re-edit';
        // IT1-W1 (saas #1): a Studio card whose project this browser no longer holds (2.5 h TTL, other
        // browser, cleared storage) has nothing to go back to. Editor re-open T3 (R-E): the key stays and
        // `reedit()` shows the expired state instead of an empty editor; "Make another video" still needs the project.
        const studioOpen = studioCard && studioProjectStillOpen(fileUpload);
        // Editor re-open T3 (R-A): ONE editor key per card -- a picker card's is "Pick another frame", so the
        // generic Re-edit goes (it opened the raw options modal). Quick convert keeps Re-edit.
        const pickerCard = !studioCard && this.framePickerNote !== null;
        const reeditShown = this.reeditButtonEnabled && !pickerCard;
        // Review 7 S1 (P3): a failed job has no output, so every key that needs one (Download, ZIP, Preview, Downloadable
        // Link, Dropbox) did nothing -- the blue Download stayed enabled over "Processing failed". The failed card offers the
        // failure's remedies instead: its editor key (Re-edit / Back to Studio / Pick another frame) as the primary, and Delete.
        const failed = this.hasFailed(fileUpload) || this.neverStarted(fileUpload);
        return <div className={styles['footer-buttons']} key={'file-list-item-' + fileUpload.id} data-jkey={`file-list-item-footer`}>
            <div className={styles['action-btns']}>
                {
                    this.zipSupported && !failed && <button
                        disabled={this.state.zipDownloadInProgress || !this.isFileReady(fileUpload) || null}
                        data-url={this.getZipFileURL(fileUpload)}
                        className={`${styles['btn']} ${pickerSequence ? `${styles['btn-primary']} ${styles['btn-touch']}` : ''} ${!fileUpload.result || (fileUpload.result && !fileUpload.result.zip) ? 'hide' : ''}`}
                        onClick={() => this.onZipDownloadBtnHandler(fileUpload)}
                        aria-busy={pickerSequence && this.state.zipDownloadInProgress ? true : undefined}
                        data-jkey={`download-btn-zip`}>
                        {this.getZipDownloadBtnText(fileUpload)}
                    </button>
                }

                {!failed && <button data-url={this.getDownloadURL(fileUpload)}
                    className={`${styles['btn']} ${pickerSequence ? '' : styles['btn-primary']}`}
                    onClick={() => this.onDownloadBtnHandler(fileUpload)}
                    disabled={!this.isFileReady(fileUpload)}
                    data-jkey={`download-btn`}>
                    {this.getFileDownloadBtnText(fileUpload)}
                </button>}

                {
                    /* Labelled, and next to Download rather than buried at the end of an
                       unlabelled icon row. On lossy image/GIF output a quarter of users
                       download nothing (see RETENTION_FIXES.md T2.4) -- they cannot judge
                       quality they were never shown. */
                    !failed && previewKindFor(fileUpload.toType) && <button
                        className={styles['btn']}
                        disabled={!this.isFileReady(fileUpload)}
                        onClick={() => this.openPreview(fileUpload)}
                        data-jkey={`preview-btn`}>
                        <img src="/icons/svg/play.svg" alt="" aria-hidden="true" /> Preview
                    </button>
                }

                {
                    false && this.hasDirectDownloadLink(fileUpload) && <div style={{ display: 'inline-block' }}>
                        <div className="g-savetodrive"
                            data-src={this.getRangeSupportedDirectDownloadLink(fileUpload)}
                            data-filename={fileUpload.originalInputFiles[0].originalName}
                            data-sitename="www.xconvert.com"
                            data-jkey={`download-drive`}>
                        </div>
                    </div>
                }

                {
                    this.hasDirectDownloadLink(fileUpload) && <Tooltip text="Save To Dropbox">
                        <button
                            className={`${styles['btn']} ${styles['btn-icon']}`}
                            disabled={!this.isFileReady(fileUpload)}
                            onClick={() => this.saveToDropbox(fileUpload.originalInputFiles[0].originalName, this.getRangeSupportedDirectDownloadLink(fileUpload))} >
                            <img className={styles['dropdox-image']} src="/img/dropbox.png" alt=""
                                data-jkey={`download-dropbox`} />
                        </button>
                    </Tooltip>
                }

                {!failed && <Tooltip text="Downloadable Link">
                    <button data-url={this.getDownloadURL(fileUpload)}
                        className={`${styles['btn']} ${styles['btn-icon']}`}
                        disabled={!this.isFileReady(fileUpload)}
                        onClick={() => this.onDirectDownloadBtnHandler(fileUpload)}
                        data-jkey={`file-list-item`}>
                        <img src="/icons/svg/link-45deg.svg" alt="" aria-hidden="true" />
                    </button>
                </Tooltip>}

                {
                    this.statusCheckButtonEnabled && <Tooltip text="Check Status">
                        <button
                            className={`${styles['btn']} ${styles['btn-icon']}`}
                            onClick={() => this.checkStatusAndShowMessage(fileUpload)}
                            data-jkey={`check-status`}>
                            <img src="/icons/svg/question.svg" alt="" aria-hidden="true" />
                        </button>
                    </Tooltip>
                }
                {
                    reeditShown && <Tooltip text={reeditLabel}>
                        <button className={`${styles['btn']}${failed ? ` ${styles['btn-primary']}` : ''}`}
                            onClick={() => this.reedit(fileUpload)}
                            data-jkey={`edit-btn`}>
                            <img src="/icons/svg/pencil-square.svg" alt="" aria-hidden="true" /> {reeditLabel}
                        </button>
                    </Tooltip>
                }
                {this.getPickAnotherFrameLink(fileUpload, failed)}
                {
                    // BUG-08: a first-class way OUT of the finished project, right beside the
                    // key that would otherwise silently continue it.
                    reeditShown && studioOpen && <Tooltip text="Make another video">
                        <button className={styles['btn']}
                            onClick={() => this.makeAnotherVideo(fileUpload)}
                            data-jkey={`make-another-video-btn`}>
                            <img src="/icons/svg/plus-circle.svg" alt="" aria-hidden="true" /> Make another video
                        </button>
                    </Tooltip>
                }
                {
                    this.deleteButtonEnabled && <Tooltip text="Delete">
                        <button className={`${styles['btn']} ${styles['btn-danger']} ${styles['btn-icon']}`}
                            onClick={() => this.removeFile(fileUpload)}
                            data-jkey={`delete-btn`}>
                            <img src="/icons/svg/trash.svg" alt="" aria-hidden="true" />
                        </button>
                    </Tooltip>
                }

            </div>
            {this.getReeditExpired(fileUpload)}
            {this.getFramePickerFooter(fileUpload)}
            {this.getNextSteps(fileUpload)}
        </div>
    }

    /**
     * Editor re-open T3 (R-E): the editor key found nothing to re-open (the picker's record is gone, the Studio project left
     * this browser). One short line and the one action that works: start a new one on the same page.
     */
    getReeditExpired(fileUpload: FileUpload): JSX.Element | null {
        const editor = this.state?.reeditExpired;
        if (!editor) return null;
        const copy = REEDIT_EXPIRED_COPY[editor];
        const href = editor === 'studio' ? this.makeAnotherVideoHref(fileUpload) : startNewPickerHref(cardSlugOf(fileUpload));
        return <div className={styles['processing-notes']} data-jkey="reedit-expired" role="status">
            <div className={styles['processing-notes-line']}>
                {copy.line}{' '}
                {href && <a href={href} className={styles['btn']} role="button" data-jkey="reedit-expired-start-new"
                    onClick={(e) => this.pickAnotherFrame(e, href)}>{copy.action}</a>}
            </div>
        </div>;
    }

    /**
     * The ZIP button's label. For a picker sequence it counts the frames — "Download all 23
     * frames (ZIP)" — and says "Making your ZIP…" while the on-demand build runs (§6.5, §0's
     * mock change list). Every other one-to-many card keeps its old label.
     */
    getZipDownloadBtnText(fileUpload: FileUpload): string {
        const ready = !!this.state.zipAwaitingClick;
        if (!this.isPickerSequence) return ready ? 'Your ZIP is ready: download' : 'Download All (Zip)';
        if (this.state.zipDownloadInProgress) return 'Making your ZIP…';
        const n = get(fileUpload, 'result.outputFiles', []).length;
        if (ready) return n > 0 ? `Your ZIP is ready: download ${n} frame${n === 1 ? '' : 's'}` : 'Your ZIP is ready: download';
        return n > 0 ? `Download all ${n} frame${n === 1 ? '' : 's'} (ZIP)` : 'Download all frames (ZIP)';
    }

    /**
     * The picker's own footer under the action row (§6.1, §6.5): "On a phone the ZIP opens in
     * the Files app." for a sequence, and "Pick another frame" for every picker card that is
     * finished — success or failure (§6.5: "The failure card is the `/downloads` failure state
     * with the §3.5 message and 'Pick another frame'"). Nothing for an ordinary conversion.
     */
    getFramePickerFooter(fileUpload: FileUpload): JSX.Element | null {
        const note = this.framePickerNote;
        if (!note) return null;
        // R14 batch 2 (ask 7, R14U-05): "Pick another frame" moved up into the action row (`getPickAnotherFrameLink`,
        // beside Re-edit); the footer keeps only the phone ZIP line.
        if (!this.isPickerSequence) return null;
        return <div className={styles['processing-notes']} data-jkey="frame-picker-footer">
            <div className={styles['processing-notes-line']} data-jkey="frame-picker-phone-line">
                On a phone the ZIP opens in the Files app.
            </div>
        </div>;
    }

    /**
     * `/<page>?tab=picker&psid=<sid>` — the sid comes from the picker's own `FRAMEPICKER-<sid>`
     * record (§6.1, §6.4), looked up by this card's `workflowId`. Without a record the link
     * still opens the picker tab and the picker shows its expired copy (D5).
     *
     * R14 batch 2 (ask 7, R14U-05): an action-row KEY -- the same `btn` class as its neighbours (13 px, ≥ 4.5:1, the
     * 44 px coarse floor), placed right after the Re-edit slot -- not an 11.5 px underlined footer line under seven
     * buttons. Still an `<a>` so a modified click (new tab) keeps the href; a plain click goes through the router.
     * Only on a picker card (`framePickerNote`), finished or failed alike (§6.5).
     */
    getPickAnotherFrameLink(fileUpload: FileUpload, primary = false): JSX.Element | null {
        if (!this.framePickerNote) return null;
        // Review 7 S1 (P3): on a failed picker card this key is the remedy, so it is the primary.
        const cls = primary ? `${styles['btn']} ${styles['btn-primary']}` : styles['btn'];
        // Editor re-open T3 (R-E): no record names this workflow -> nothing to re-open; the key shows the expired state.
        const route = this.reeditRoute(fileUpload);
        if (route.kind !== 'picker') {
            if (!cardSlugOf(fileUpload)) return null;
            return <button className={cls}
                data-jkey="pick-another-frame"
                onClick={() => this.reedit(fileUpload)}>
                <img src="/icons/svg/arrow-counterclockwise.svg" alt="" aria-hidden="true" /> Pick another frame
            </button>;
        }
        const href = route.href;
        return <a href={href}
            className={cls}
            role="button"
            data-jkey="pick-another-frame"
            onClick={(e) => this.pickAnotherFrame(e, href)}>
            <img src="/icons/svg/arrow-counterclockwise.svg" alt="" aria-hidden="true" /> Pick another frame
        </a>;
    }

    /** The SEEK instant this card's job asked ffmpeg for (the record's `frameTimes`/`thumbs` key): the request's `screenshotTime`, else the note's `t=`. */
    private pickerSeekOf(fileUpload: FileUpload): number | string | null {
        const raw = (fileUpload?.options as Record<string, unknown> | undefined)?.screenshotTime;
        if (typeof raw === 'string' || typeof raw === 'number') return raw;
        return framePickerNoteOf(fileUpload?.result?.processingNotes)?.t ?? null;
    }

    /**
     * R14 batch 2 (ask 9, review BUG-015): the frame the user picked, ON the card -- for a ONE-frame and a SHEET
     * result (a sequence has many outputs and no single poster). Source order (`pickerThumbSource`): the edge's
     * poster of the delivered file once the result has landed (`outputPosterUrl`, the same endpoint the preview
     * modal falls back to), the picker's own picture of the pressed frame (`record.thumbs`, ask 10 -- absent until
     * the picker's slice lands) while the job is still queued; the box is reserved as soon as the note is known so
     * the card does not shift when the picture arrives. `onerror` on the edge poster falls back to the pressed
     * frame, and with none the box goes (an audio or archive output the thumb endpoint 404s on).
     */
    getFramePickerThumb(fileUpload: FileUpload): JSX.Element | null {
        const note = this.framePickerNote;
        if (!note || note.mode === 'SEQUENCE' || note.mode === 'PICKED' || this.state.pickerThumbHidden) return null;
        const recordThumb = note.mode === 'ONE' ? pickerThumbForWorkflow(fileUpload?.workflowId, this.pickerSeekOf(fileUpload), this.storageService) : null;
        const source = pickerThumbSource(this.state.pickerThumbEdgeFailed ? null : this.outputPosterUrl(fileUpload), recordThumb);
        return <div className={styles['frame-picker-thumb-box']} data-jkey="frame-picker-thumb-box" data-source={source?.kind ?? 'none'}>
            {source && <img
                className={styles['frame-picker-thumb']}
                data-jkey="frame-picker-thumb"
                src={source.src}
                alt={note.mode === 'SHEET' ? 'The contact sheet' : 'The frame you picked'}
                loading="lazy"
                decoding="async"
                onError={() => this.onPickerThumbError(source.kind, source.fallback)} />}
        </div>;
    }

    private onPickerThumbError(kind: 'edge' | 'record', fallback: string | null) {
        if (kind === 'edge' && fallback) this.setState({ pickerThumbEdgeFailed: true });
        else this.setState({ pickerThumbHidden: true });
    }

    /** The href alone, so a spec can read it without rendering. */
    pickAnotherFrameHref(fileUpload: FileUpload): string | null {
        const slug = fileUpload?.pageData?.path || fileUpload?.uniqueProcessType;
        let sid: string | null = null;
        try {
            sid = pickerSidForWorkflow(fileUpload?.workflowId, this.storageService);
        } catch { /* a link without a sid is still a link */ }
        return pickAnotherFrameHref(slug, sid, fileUpload?.workflowId);
    }

    /**
     * Through Next's router, never raw history, exactly as `openStudioReedit` does for the
     * Studio (`components/studio/export/reedit.ts`): `/downloads` → a tool page is a real
     * navigation. Modified clicks are the user asking for a new tab and are left to the anchor;
     * a router that has not mounted throws, and then the anchor's own href navigates.
     */
    private pickAnotherFrame = (e: any, href: string) => {
        if (e?.metaKey || e?.ctrlKey || e?.shiftKey || e?.button === 1) return;
        try {
            const result = Router.push(href);
            e?.preventDefault?.();
            if (result && typeof (result as PromiseLike<unknown>).then === 'function') {
                void Promise.resolve(result as PromiseLike<unknown>).catch(() => undefined);
            }
        } catch {
            // No mounted router: let the anchor navigate.
        }
    };

    /**
     * The time badge (§6.5): the frame a one-frame card holds. Nothing for a sequence, a sheet, or a one-frame line
     * without a time.
     */
    getFramePickerTimeBadge(): JSX.Element | null {
        const note = this.framePickerNote;
        if (!note || note.mode !== 'ONE' || note.t === null) return null;
        // Round 4 (R4-P1 copy pass): the badge SAYS what the number is -- a bare "0:01.567" beside the
        // file name read as noise to a first-time user; the accessible name is unchanged.
        // R14 batch 2 (ask 8, BUG-026) + R14Q-14/15/16: "Frame 44 of 240 · 0:01.433" -- the SERVER's frame and time once
        // the result has landed (`frame=`/`at=` on the note, the `f0044-0_01.433` of the saved name), the picker's own
        // containing-frame rule before that; "of N" is the counter's count; time-only for VFR / unknown rate (`frameBadgeText`).
        const fu = this.props.fileUpload;
        const label = pickerFrameLabelForWorkflow(fu?.workflowId, this.frameBadgeFactsOf(fu), this.storageService);
        if (label === null) return null;
        return <span
            className={styles['frame-picker-time-badge']}
            data-jkey="frame-picker-time-badge"
            aria-label={label}
            title={label}>
            {label}
        </span>;
    }

    /**
     * R14 (R14U-18): the lead line of a SEQUENCE / SHEET card, in the title row where the ONE-frame card's time badge sits --
     * "Contact sheet · 12 frames · 0:02.000 – 0:04.000", "Frames · 10 · 0:02.000 – 0:04.000". The words are the card module's
     * (`rangeBadgeText`): the picker's record first (the user's own range and the count the panel showed), the server's landed
     * line second, a sequence's output count third. Same chip class as the time badge; nothing for ONE or an ordinary card.
     */
    getFramePickerRangeBadge(): JSX.Element | null {
        const note = this.framePickerNote;
        if (!note || note.mode === 'ONE') return null;
        const fu = this.props.fileUpload;
        const parts = pickerRangeBadgeForWorkflow(fu?.workflowId, note, fu?.result, this.storageService);
        if (parts === null) return null;
        // R14U-25: the DOM text, `aria-label` and `title` are the full line at every width. Under 768 px the module's
        // CSS hides the lead word and draws `data-short-lead` before the rest ("Sheet · 10 frames · 0:00.000 – 0:04.500")
        // and the title row wraps the chip under the title instead of clipping it; the times are never shortened.
        return <span
            className={styles['frame-picker-time-badge']}
            data-jkey="frame-picker-range-badge"
            aria-label={parts.text}
            title={parts.text}>
            <span className={styles['frame-picker-range-badge-text']} data-short-lead={parts.shortLead}>
                <span className={styles['frame-picker-range-badge-lead']}>{parts.lead}</span>{parts.rest}
            </span>
        </span>;
    }

    /**
     * IT1 (W3 ← W4 be #8): a contact sheet whose note says `labelsDropped=true` came without its time stamps and title
     * (this edge's ffmpeg has no `drawtext`); the card says so in one line, once, under the title. Nothing otherwise.
     * R14 (R14U-18): demoted to a quiet one-line note under the title row's badge (`frame-picker-labels-note`, 12 px muted) --
     * the badge leads with what the sheet is; the wording is the picker's copy table's, unchanged.
     */
    getFramePickerLabelsNote(): JSX.Element | null {
        const note = this.framePickerNote;
        if (!note || note.mode !== 'SHEET' || note.labelsDropped !== true) return null;
        return <span className={styles['frame-picker-labels-note']} data-jkey="frame-picker-labels-note" role="note">
            {FRAME_PICKER_LABELS_DROPPED_COPY}
        </span>;
    }

    /**
     * "What next?" for this specific file, on this specific card.
     *
     * It used to be one block at the bottom of /downloads derived from the newest result only.
     * With three finished files that is ambiguous -- which one does it act on? -- and measured on
     * QA at 412px it rendered at y=2,420 against a first card at y=501, so nobody reached it.
     * A continuation belongs beside the Download button for the file it continues.
     */
    /**
     * Has this file's conversion actually failed?
     *
     * `status === FILEUPLOAD_STATE_FAILED` only covers a failed UPLOAD, so it is false for a
     * file that uploaded fine and then failed to convert -- which is most failures. Anything
     * deciding whether to show success-shaped UI needs all three signals, not just that one.
     */
    hasFailed(fileUpload: FileUpload): boolean {
        return fileUpload?.status === this.FILEUPLOAD_STATE_FAILED
            || fileUpload?.result?.error === true
            || fileUpload?.ws?.processStageSubState === ProcessStageSubStateTypes.FAILED;
    }

    /**
     * The page that PRODUCED this file, which the continuation must never offer back.
     *
     * It is not the component's own route. A finished conversion pushes the user to
     * `/downloads` (`components/converter/file-convert.component.tsx:601`), so `this.props.path`
     * is 'downloads' by the time this card renders and would exclude nothing. The producing
     * page travels on the record instead, and this is the exact expression the convert metrics
     * already use for their `page` attribute (`services/file-manager.service.ts:503`), so the
     * chip set and the funnel agree on what "the page you are on" means.
     *
     * Decision provenance (QA pass 2 row 31, measured 2026-09-02): the call site used to read
     * `(fileUpload as any).pageSlug` — a field `FileUpload` does not carry — so `suggestionsFor`'s
     * documented "always excluded" never fired. A compress-pdf result spent both of its two
     * chips on [Compress PDF] [Merge PDF] and `Edit PDF` was pushed out of the window, which is
     * why the spec's §11.1 row 31 continuation could not be reached at all.
     */
    private producingSlug(fileUpload: FileUpload): string {
        return `${fileUpload?.pageData?.path || fileUpload?.uniqueProcessType || ''}`;
    }

    getNextSteps(fileUpload: FileUpload) {
        if (this.hasFailed(fileUpload) || this.neverStarted(fileUpload)) return null;
        // Review 7 M1-03: the row used to mount only once the result landed, adding ~38 px under the keys at the very moment
        // the card went live and shifting everything below it (CLS ~0.1 per card). While the job runs the SAME row is laid
        // out but hidden (`visibility: hidden`: no paint, no focus, no taps), so completing only reveals it.
        const pending = !this.isFileReady(fileUpload);
        const steps = suggestionsFor(fileUpload.toType, this.producingSlug(fileUpload), 3)
            .filter(t => !t.requiresBrowserDecode || this.editorSafe === true)
            .filter(t => t.slug !== EDIT_PDF_SLUG || this.pdfEditorEnabled === true)
            .slice(0, 2);
        if (!steps.length) return null;

        return <div className={styles['next-steps']} data-jkey={pending ? 'card-next-steps-reserve' : 'card-next-steps'}
            aria-hidden={pending || undefined} style={pending ? { visibility: 'hidden' } : undefined}>
            <span className={styles['next-steps-label']}>Next:</span>
            {steps.map((t, i) => (
                <a key={t.slug} href={`/${t.slug}`}
                    className={styles['next-step-chip']}
                    aria-busy={this.chaining === t.slug}
                    onClick={(e) => this.chainClick(e, fileUpload, t.slug, i)}>
                    {this.chaining === t.slug ? 'Taking your file…' : t.label}
                </a>
            ))}
            {this.chainNotice && (
                <span role="status" className={styles['next-steps-notice']}>{this.chainNotice}</span>
            )}
        </div>;
    }

    /**
     * Carry this card's output to the next tool. Modified clicks are left alone -- those are the
     * user asking for a new tab -- and every failure still navigates, after saying why.
     */
    private chainClick = async (e: any, fileUpload: FileUpload, slug: string, rank: number) => {
        if (e?.metaKey || e?.ctrlKey || e?.shiftKey || e?.button === 1) return;
        e.preventDefault();
        if (this.chaining) return;
        this.chaining = slug;
        this.chainNotice = null;
        this.forceUpdate();

        try {
            container.get(MetricsService).emit(MetricEventName.NEXT_TOOL_CLICKED, { toSlug: slug, rank });
        } catch { /* never load-bearing */ }

        let downloadUrl: string | null = null;
        try {
            const output = fileUpload?.result?.output;
            // Multi-output results have no single file to carry; the chip still navigates.
            if (output && !Array.isArray(output)) {
                downloadUrl = this.getRangeSupportedDirectDownloadLink(fileUpload, true);
            }
        } catch { /* fall through to an uncarried navigation */ }

        const result = await chainToTool(fileUpload, slug, downloadUrl);
        try {
            container.get(MetricsService).emit(MetricEventName.NEXT_TOOL_CHAINED, {
                toSlug: slug, carried: result.carried, reason: result.reason || '',
            });
        } catch { /* never load-bearing */ }

        if (!result.carried) {
            this.chaining = null;
            this.chainNotice = result.reason === 'too-large'
                ? 'Too large to carry over — pick it again on the next page.'
                : 'Couldn\'t bring the file across — pick it again on the next page.';
            this.forceUpdate();
            await new Promise(r => setTimeout(r, 1800));
        }
        window.location.assign(result.href);
    };

    /** Last seen progress %, kept so the bar survives interleaved stage-text messages. */
    private latchedProgress: { pct: number, stage: string } | null = null;

    render() {
        const fu = this.props.fileUpload;
        const ws = fu.ws;
        // Latch progress across interleaved notification messages: the backend alternates
        // percentage and stage-text updates, and unmounting the bar on every text message
        // made the card collapse/expand constantly (owner UX report). The bar mounts on the
        // first percentage and stays until a terminal state, showing the last known value
        // (monotonic within a stage so multi-pass encodes don't run the bar backwards).
        const neverStarted = this.neverStarted(fu);
        const inFlight = !neverStarted && fu.status !== this.FILEUPLOAD_STATE_FAILED
            && (!ws || ws.processStageSubState !== ProcessStageSubStateTypes.FAILED)
            && !(fu.result && fu.result.output)
            && (!ws || ws.processStage !== 'completed');
        if (ws && ((ws.processStage === 'processing' && ws.dataType === 'percentage')
            || (ws.processStage === 'upload' && ws.dataType === 'notification'))) {
            const v = Number(this.decodeData(fu));
            if (!isNaN(v)) {
                if (!this.latchedProgress || this.latchedProgress.stage !== ws.processStage) {
                    this.latchedProgress = { pct: v, stage: ws.processStage };
                } else {
                    this.latchedProgress.pct = Math.max(this.latchedProgress.pct, v);
                }
            }
        }
        const shouldShowProgressbar = inFlight && this.latchedProgress != null;
        // The queued machine's verdict for THIS card, or null when it has
        // nothing to say (flag off, card never queued, or work has started).
        const scheduleView = scheduleCoordinator.enabled ? scheduleCoordinator.view(fu.id) : null;
        return <div className={`id${this.props.fileUpload.id} ${styles['card']}`}>
            <div className={styles['card-body']} data-jkey={`file-list-item-card`}>

                <div className={`${styles['card-title']}`} data-jkey={`file-list-item-title`}>
                    {this.getOutputFileNameIfAvailable(this.props.fileUpload)}
                    {this.getFramePickerTimeBadge()}
                    {this.getFramePickerRangeBadge()}
                </div>
                {this.getFramePickerLabelsNote()}
                <div className={styles['card-content']} >
                    {this.getFramePickerThumb(fu)}
                    {/* ONE always-mounted status row while the card is not failed. The old
                        three mutually-exclusive blocks swapped in and out per websocket
                        message (loader + text vs text vs nothing), which read as the card
                        collapsing and expanding. Same text-selection precedence as before:
                        local readableMessage outside processing, else the decoded ws
                        message, else the pre-ws placeholder. */}
                    {/* Hands-off scheduling (clientQueuedState; plan §4.4,
                        corrections 16/22). The card owns the whole story now:
                        "Queued — server busy, retrying (n of N)" while the
                        ladder runs, then the amber "Not started — your file is
                        still here". No modal, no per-file Retry button — the
                        one "Retry all" lives on the batch header, and the file
                        itself is never deleted to get here. */}
                    {
                        scheduleView && !scheduleView.stalled
                        && <div className={styles['schedule-queued']} data-jkey="file-list-item-queued">
                            <div className={styles['status-loader']}>
                                <RippleLoaderComponent size={13}></RippleLoaderComponent>
                            </div>
                            <span>{scheduleView.message}</span>
                        </div>
                    }
                    {
                        scheduleView && scheduleView.stalled
                        && <div className={styles['schedule-stalled']} data-jkey="file-list-item-stalled" role="status">
                            <span>{scheduleView.message}</span>
                        </div>
                    }

                    {
                        neverStarted
                        && <div className={styles['conversion-error']} data-jkey="file-list-item-never-started" role="status">
                            {NEVER_STARTED_COPY}
                        </div>
                    }

                    {
                        !scheduleView
                        && !neverStarted
                        && this.props.fileUpload.status !== this.FILEUPLOAD_STATE_FAILED
                        && (!ws || ws.processStageSubState !== ProcessStageSubStateTypes.FAILED)
                        // A recovered/completed card with no live ws has nothing to say —
                        // matches the old first block, which hid once a result existed.
                        && ((ws && ws.processStage) || !fu.result)
                        && <div className={styles['status-line']} data-jkey="file-list-item-status">
                            {
                                inFlight
                                && <div className={styles['status-loader']}>
                                    <RippleLoaderComponent size={13}></RippleLoaderComponent>
                                </div>
                            }
                            <span>
                                {
                                    fu.readableMessage
                                        && (!ws || (ws.processStage !== 'processing' && ws.dataType !== 'percentage'))
                                        ? fu.readableMessage
                                        : ws && ws.processStage
                                            ? this.decodeDataType(fu, ws.dataType, ws.data)
                                            : fu.readableMessage || 'Process starting...'
                                }
                            </span>
                        </div>
                    }

                    {
                        this.props.fileUpload.ws
                        && this.props.fileUpload.ws.processStageSubState === ProcessStageSubStateTypes.FAILED
                        && this.props.fileUpload.ws.dataType !== 'enforcement_error'
                        && <div className={styles['conversion-error']}>
                            {this.props.fileUpload.ws.data || 'Conversion failed.'}
                        </div>
                    }

                    {
                        this.props.fileUpload.ws
                        && this.props.fileUpload.ws.processStageSubState === ProcessStageSubStateTypes.FAILED
                        && this.props.fileUpload.ws.dataType === 'enforcement_error'
                        && <div className={styles['conversion-error']}>
                            {this.decodeDataType(this.props.fileUpload, 'enforcement_error', this.props.fileUpload.ws.data)}
                            {(() => { try { const d = JSON.parse(this.props.fileUpload.ws.data); return d.upgradeRequired ? <span> — <a href="/product" style={{color: '#007bff', textDecoration: 'underline'}}>Upgrade your plan</a></span> : <span> — Try again later.</span>; } catch { return null; } })()}
                        </div>
                    }

                    {
                        this.props.fileUpload.status === this.FILEUPLOAD_STATE_FAILED
                        && <div>
                            Upload failed...
                        </div>
                    }

                    {/* Which settings THIS run used — repeated runs of one file were
                        indistinguishable without it (sweep P2 UX). Silent on defaults. */}
                    {(() => {
                        const echo = this.optionEchoOf(this.props.fileUpload);
                        return echo ? <div className={styles['option-echo']}>{echo}</div> : null;
                    })()}

                    {this.getResultLinesReserve(this.props.fileUpload)}

                    {
                        this.props.fileUpload.result
                        && this.props.fileUpload.result.sizes
                        && this.props.fileUpload.result.sizes.outputs
                        && <div>
                            {this.getProcessInfo(this.props.fileUpload)}
                        </div>
                    }

                    {
                        this.props.fileUpload.result
                        && this.props.fileUpload.result.sizes
                        && this.props.fileUpload.result.sizes.outputs
                        && this.isSingleFile(this.props.fileUpload.result.sizes.outputs)
                        && <div>
                            {this.getOutputInfo(this.props.fileUpload)}
                        </div>
                    }

                    {
                        this.hasZipFileInfo
                        && <div>
                            {this.hasZipFileData}
                        </div>
                    }

                    {(() => {
                        // The picker's `framePicker mode=… t=…` line is for the machine (the badge,
                        // the ZIP primary, the link, `FILE_DOWNLOAD{source}`); the human never sees
                        // it raw. Everything else still renders, one line per note.
                        const notes = withoutFramePickerNotes(this.props.fileUpload.result?.processingNotes);
                        return notes.length > 0
                            && <div className={styles['processing-notes']}>
                                {/* Every note renders. The old single-line + hover-tooltip form hid all
                                    but the first note — including the best-effort disclosure ("Reaching
                                    X would destroy this GIF…"), the one line the pre-convert copy
                                    explicitly promises ("we'll tell you if that happens"). A tooltip is
                                    not telling: mobile has no hover and desktop users don't know to. */}
                                {notes.map((note, i) => (
                                    <div key={i} className={styles['processing-notes-line']}>ⓘ {note}</div>
                                ))}
                            </div>;
                    })()}

                    {

                        shouldShowProgressbar && <ProgressComponent
                            value={this.latchedProgress ? this.latchedProgress.pct : 0}
                            postFix='%'
                        >
                        </ProgressComponent>
                    }
                </div>
                {
                    !this.props.hideFooterButtons && this.getFooterButtons(this.props.fileUpload)
                }

            </div>
            {!this.hasFailed(this.props.fileUpload) &&
            <div className={styles['social-share-promote']}>
                {this.getShareCTA()}
                <div className={styles['social-share']}>
                    <div className={`${styles['share-btn']} ${styles['share-btn-primary']}`}
                        onClick={() => this.copyLink()}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/><path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z"/></svg>
                        {this.state.linkCopied ? 'Copied!' : 'Copy Link'}
                    </div>
                    <div className={`${styles['share-btn']} ${styles['share-btn-credit']}`}
                        onClick={() => this.toggleCredit()}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M10.478 1.647a.5.5 0 1 0-.956-.294l-4 13a.5.5 0 0 0 .956.294l4-13zM4.854 4.146a.5.5 0 0 1 0 .708L1.707 8l3.147 3.146a.5.5 0 0 1-.708.708l-3.5-3.5a.5.5 0 0 1 0-.708l3.5-3.5a.5.5 0 0 1 .708 0zm6.292 0a.5.5 0 0 0 0 .708L14.293 8l-3.147 3.146a.5.5 0 0 0 .708.708l3.5-3.5a.5.5 0 0 0 0-.708l-3.5-3.5a.5.5 0 0 0-.708 0z"/></svg>
                        Add us to your site
                    </div>
                    <div className={styles['share-btn']}
                        onClick={() => this.shareOnWhatsApp()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                    </div>
                    <div className={styles['share-btn']}
                        onClick={() => this.shareOnReddit()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF4500" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0zm4.388 3.199c1.104 0 1.999.895 1.999 1.999 0 1.105-.895 2-1.999 2-.946 0-1.739-.657-1.947-1.539v.002c-1.147.162-2.032 1.15-2.032 2.341v.007c1.776.067 3.4.567 4.686 1.363.473-.363 1.064-.58 1.707-.58 1.547 0 2.802 1.254 2.802 2.802 0 1.117-.655 2.081-1.601 2.531-.088 3.256-3.637 5.876-7.997 5.876-4.361 0-7.905-2.617-7.998-5.87-.954-.447-1.614-1.415-1.614-2.538 0-1.548 1.255-2.802 2.803-2.802.645 0 1.239.218 1.712.585 1.275-.79 2.881-1.291 4.64-1.365v-.01c0-1.663 1.263-3.034 2.88-3.207.188-.911.993-1.595 1.959-1.595zM12 9.8c-3.446 0-6.24 1.791-6.24 4 0 2.21 2.794 4 6.24 4 3.447 0 6.241-1.79 6.241-4 0-2.209-2.794-4-6.241-4zm-3.44 1.4c.662 0 1.2.538 1.2 1.2 0 .662-.538 1.2-1.2 1.2-.663 0-1.2-.538-1.2-1.2 0-.662.537-1.2 1.2-1.2zm6.88 0c.663 0 1.2.538 1.2 1.2 0 .662-.537 1.2-1.2 1.2-.662 0-1.2-.538-1.2-1.2 0-.662.538-1.2 1.2-1.2zm-5.26 3.843c.155-.156.408-.156.563 0 .586.585 1.679.635 2.257.635.578 0 1.671-.05 2.256-.635a.398.398 0 0 1 .563 0 .398.398 0 0 1 0 .563c-.789.789-2.214.851-2.819.851-.605 0-2.03-.062-2.82-.851a.398.398 0 0 1 0-.563z"/></svg>
                        Reddit
                    </div>
                    <div className={styles['share-btn']}
                        onClick={() => this.openTwitter()}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.738 2h1.994L9.377 7.083 14.5 14h-4.011L7.346 9.805 3.751 14H1.757l4.658-5.437L1.5 2h4.113l2.84 3.834L11.738 2Zm-.7 10.781h1.105l-7.13-9.627H3.828l7.21 9.627Z" fill="#161616"/></svg>
                    </div>
                </div>
                {this.state.creditOpen && this.getCreditPanel()}
            </div>}
        </div>
    }


    getShareCTA(): JSX.Element {
        const fu = this.props.fileUpload;
        const saved = this.getSavedBytes();
        // The picker's machine line is not a caveat about the run; only human notes count.
        const ranClean = withoutFramePickerNotes(fu.result?.processingNotes).length === 0;
        // Compress: lead with the number only when it's actually impressive —
        // a real percentage AND enough absolute bytes that it isn't comic
        // ("we saved you 1.03 KB").
        if (saved != null && saved.reduction > 5 && saved.bytes > 100 * 1024) {
            return <span>💚 We just saved you <b>{toReadableBytes(saved.bytes, 'quantity')}</b>, free. Help the next person find us.</span>;
        }
        if (ranClean) {
            switch (fu.processType) {
                case ProcessTypes.CONVERT:
                    return <span>💚 Converted free — no watermark, no signup. Help the next person find us.</span>;
                case ProcessTypes.MERGE:
                    return <span>💚 Merged into one file, free. Help others find us.</span>;
                case ProcessTypes.SPLIT:
                case ProcessTypes.EXTRACT:
                    return <span>💚 Done in seconds, free. Know someone who needs this?</span>;
                case ProcessTypes.RESIZE:
                case ProcessTypes.CROP:
                    return <span>💚 Resized free, no watermark. Help others find us.</span>;
            }
        }
        return <span>💚 This tool is free because people share it. Pass it on.</span>;
    }

    /** Bytes saved for compress jobs; null when not applicable. */
    getSavedBytes(): { bytes: number, reduction: number } | null {
        const fu = this.props.fileUpload;
        if (fu.processType !== ProcessTypes.COMPRESS || !fu.result?.sizes?.outputs) return null;
        try {
            const key = Object.keys(fu.result.sizes.outputs)[0];
            const outputSize = parseFloat(fu.result.sizes.outputs[key]);
            const reduction = Math.round(100 * (1 - outputSize / fu.size));
            if (!isNaN(reduction) && reduction > 0) return { bytes: fu.size - outputSize, reduction };
        } catch { }
        return null;
    }

    trackShare(channel: string) {
        try {
            this.metricsService.emit(MetricEventName.SHARE_CLICKED, {
                channel,
                tool: this.props.fileUpload.pageData?.uniqueProcessType || 'unknown',
            });
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.BUTTON,
                GAContants.EventAction['SHARE-CLICK'],
                GAContants.EventLabel.NAVIGATE,
                `${channel}:${this.props.fileUpload.pageData?.uniqueProcessType || 'unknown'}`,
            );
        } catch { }
    }

    toggleCredit() {
        if (!this.state.creditOpen) {
            this.trackShare('credit-open');
            this.metricsService.emit(MetricEventName.CREDIT_PANEL_OPENED, {
                tool: this.props.fileUpload.pageData?.uniqueProcessType || 'unknown',
            });
        }
        this.setState({ creditOpen: !this.state.creditOpen, creditCopied: false });
    }

    /** A few anchor phrasings so thousands of pasted credits don't share one
        identical footprint. Stable per file (keyed on name length) but varied
        across users. */
    getCreditSnippet(format: 'html' | 'markdown' | 'plain'): string {
        const fu = this.props.fileUpload;
        const tool = fu.pageData?.uniqueProcessType || '';
        const url = `https://www.xconvert.com/${tool}`;
        // Derive the display name from the SLUG, never the SEO title — titles
        // carry marketing tails ("Compress PNG Free — No Limits…") that make
        // terrible anchor text.
        const small = ['to', 'a', 'an', 'the', 'into', 'and'];
        const toolName = tool
            ? tool.split('-').map(w => small.includes(w) ? w
                : w.length <= 4 ? w.toUpperCase()
                    : w[0].toUpperCase() + w.slice(1)).join(' ')
                .replace(/^Convert /, '')
            : 'XConvert';
        const saved = this.getSavedBytes();
        const verb = fu.processType === ProcessTypes.MERGE ? 'Merged my files with '
            : fu.processType === ProcessTypes.SPLIT || fu.processType === ProcessTypes.EXTRACT ? 'Split my file with '
                : fu.processType === ProcessTypes.RESIZE || fu.processType === ProcessTypes.CROP ? 'Resized my image with '
                    : fu.processType === ProcessTypes.COMPRESS ? 'Compressed my file with '
                        : 'Converted my file with ';
        const stats = (saved && saved.reduction > 5 && saved.bytes > 100 * 1024)
            ? `Compressed ${toReadableBytes(fu.size, 'quantity')} → ${toReadableBytes(fu.size - saved.bytes, 'quantity')} (−${saved.reduction}%) with `
            : verb;
        const anchors = [
            `the free ${toolName} tool on XConvert`,
            `XConvert's free ${toolName} tool`,
            `this free online ${toolName} tool`,
            `${toolName} at xconvert.com`,
        ];
        const anchor = anchors[(fu.name || '').length % anchors.length];
        if (format === 'html') return `${stats}<a href="${url}">${anchor}</a>.`;
        if (format === 'markdown') return `${stats}[${anchor}](${url}).`;
        return `${stats}${anchor}: ${url}`;
    }

    copyCredit() {
        navigator.clipboard.writeText(this.getCreditSnippet(this.state.creditFormat)).then(() => {
            this.trackShare(`credit-copy-${this.state.creditFormat}`);
            this.metricsService.emit(MetricEventName.CREDIT_COPIED, {
                format: this.state.creditFormat,
                tool: this.props.fileUpload.pageData?.uniqueProcessType || 'unknown',
            });
            this.setState({ creditCopied: true });
            setTimeout(() => this.setState({ creditCopied: false }), 2000);
        });
    }

    getCreditPanel(): JSX.Element {
        const formats: Array<'html' | 'markdown' | 'plain'> = ['html', 'markdown', 'plain'];
        const labels = { html: 'HTML', markdown: 'Markdown', plain: 'Plain' };
        return <div className={styles['credit-panel']}>
            <div className={styles['credit-panel-lead']}>
                Have a blog, class page, wiki, or README? One pasted line does more for
                us than anything else — it&apos;s how new people find a free tool with no ad budget.
            </div>
            <div className={styles['credit-tabs']}>
                {formats.map(f => (
                    <span key={f}
                        className={`${styles['credit-tab']} ${this.state.creditFormat === f ? styles['credit-tab-active'] : ''}`}
                        onClick={() => this.setState({ creditFormat: f, creditCopied: false })}>
                        {labels[f]}
                    </span>
                ))}
            </div>
            <div className={styles['credit-snippet-row']}>
                <code className={styles['credit-snippet']}>{this.getCreditSnippet(this.state.creditFormat)}</code>
                <div className={`${styles['share-btn']} ${styles['share-btn-primary']}`} onClick={() => this.copyCredit()}>
                    {this.state.creditCopied ? 'Copied!' : 'Copy'}
                </div>
            </div>
            <div className={styles['credit-panel-foot']}>
                No website? Sharing with one person helps too. 🙏 &nbsp;
                <a href={`/link-us?tool=${this.props.fileUpload.pageData?.uniqueProcessType || ''}`}>More ways to link us →</a>
            </div>
        </div>;
    }

    copyLink() {
        const url = `https://www.xconvert.com/${this.props.fileUpload.pageData?.uniqueProcessType || ''}`;
        navigator.clipboard.writeText(url).then(() => {
            this.trackShare('copy-link');
            this.setState({ linkCopied: true });
            setTimeout(() => this.setState({ linkCopied: false }), 2000);
        });
    }

    shareOnWhatsApp() {
        this.trackShare('whatsapp');
        const url = `https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}`;
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(this.props.fileUpload.pageData.title + ' ' + url)}`, 'popupwindow', 'scrollbars=yes,width=700,height=400')?.focus();
    }

    shareOnReddit() {
        this.trackShare('reddit');
        const url = `https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}`;
        window.open(
            `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(this.props.fileUpload.pageData.title)}`,
            'popupwindow',
            'scrollbars=yes,width=900,height=650',
        )?.focus();
    }

    openTwitter() {
        this.trackShare('x');
        var popUp = window.open(`https://twitter.com/intent/tweet?url=https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}&text=${encodeURIComponent(this.props.fileUpload.pageData.title)}&via=xconvert_com&hashtags=xconvert`,
            'popupwindow',
            'scrollbars=yes,width=700,height=400'
        );
        popUp.focus();
        return false
    }

}
